import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Box,
  Button,
  Grid,
  Chip,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Stack,
  Popover,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  Menu,
  MenuItem,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Autocomplete,
  Pagination,
  Collapse,
  Skeleton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  Person,
  Email,
  Phone,
  Business,
  Work,
  CalendarToday,
  AccessTime,
  Face,
  Image as ImageIcon,
  ExpandMore,
  ExpandLess,
  CalendarMonth,
  FileDownload,
  ArrowDropDown,
  ArrowUpward,
  ArrowDownward,
  ZoomIn,
  DirectionsRun as DirectionsRunIcon,
  Assessment as AssessmentIcon,
  ShowChart,
  ViewTimeline,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { getFaceImage, authenticatedFetch, exportAttendanceLogs, getActivityGroupTimeline, getActivityGroupCameraAnalysis, getAuditLogs, getAuditImageBlob, getActivityLogs, getActivityNames, getActivityCameras, exportAuditLogs, exportActivityLogs, getPersonById } from '../utils/api';
import { hydrateTimingDialogFromDetail } from '../utils/auditTimingDialog';
import { peek as peekAuditImage, getOrFetch as fetchAuditImage } from '../services/auditImageCache';
import { DayTimelineBar } from './AuditAttendanceGridLayout';
import { AttendancePresenceGraph } from './AttendancePresenceGraph';
import TodayAttendanceMetricsCards from './TodayAttendanceMetricsCards';
import { useImagePreview } from './ImagePreviewDialog';
import { formatDateWithSeconds } from '../utils/dateUtils';
import { ActivityTimelineInline } from './ActivityLogs';
import { AuditTile, getResultMeta } from './Audit';
import { AuditProcessingTimelineModal } from './AuditDialogs';
import { DateRangePicker, Calendar } from 'react-date-range';
import { format, addDays, isSameDay } from 'date-fns';
import ClearAllFiltersButton from './ClearAllFiltersButton';
import {
  pageFilterActionButtonSx,
  pageFilterDatePickerSx,
  pageFilterFormControlSx,
  pageFiltersActionsSx,
  pageFiltersControlsSx,
  pageFiltersStackSx,
  pageFiltersToolbarSx,
} from '../utils/pageFilterStyles';
import {
  createTodayDateRange,
  DEFAULT_END_TIME,
  DEFAULT_START_TIME,
  isDefaultDateTimeRange,
} from '../utils/filterDefaults';
import { keyframes } from '@mui/system';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import UniversalModal from './UniversalModal';
import * as XLSX from 'xlsx';

// Helper functions for building stack images and timestamps
const MAX_STACK_PREVIEW = 3;

const normaliseMs = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  return Math.trunc(num);
};

const msToIsoString = (value) => {
  const num = normaliseMs(value);
  if (num === null) return '';
  const date = new Date(num);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
};

const parseIsoToEpochMs = (value) => {
  if (!value && value !== 0) return Number.NaN;
  const text = String(value).trim();
  if (!text) return Number.NaN;
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? Number.NaN : date.getTime();
};

const buildSelectionRange = (startDate, endDate) => ({
  startDate,
  endDate,
  key: 'selection'
});

const EMPLOYEE_DETAIL_DATE_SECTIONS = ['attendance', 'activity', 'logs', 'activity_logs'];

const searchHasDateParams = (search) => {
  const params = new URLSearchParams(search || '');
  return params.has('start_date') || params.has('end_date');
};

const getDateRangeFromSearch = (search, fallbackStart, fallbackEnd) => {
  const params = new URLSearchParams(search || '');
  const startDateParam = params.get('start_date');
  const endDateParam = params.get('end_date');

  if (!startDateParam && !endDateParam) {
    return [buildSelectionRange(fallbackStart, fallbackEnd)];
  }

  const parsedStart = startDateParam ? new Date(startDateParam) : fallbackStart;
  const parsedEnd = endDateParam ? new Date(endDateParam) : parsedStart;

  if (Number.isNaN(parsedStart.getTime()) || Number.isNaN(parsedEnd.getTime())) {
    return [buildSelectionRange(fallbackStart, fallbackEnd)];
  }

  return [buildSelectionRange(parsedStart, parsedEnd)];
};

const getDefaultDateRangeForSection = (section) => {
  const today = new Date();
  const sixDaysAgo = new Date(today);
  sixDaysAgo.setDate(today.getDate() - 6);

  if (section === 'attendance' || section === 'activity') {
    return [buildSelectionRange(sixDaysAgo, today)];
  }

  return [buildSelectionRange(today, today)];
};

const getSessionStorageKey = (personId, section) => {
  if (!personId || personId === 'employee-details') {
    return null;
  }
  return `employeeDetails:${personId}:${section}`;
};

const loadSectionDateRangeFromSession = (personId, section) => {
  const key = getSessionStorageKey(personId, section);
  if (!key) {
    return null;
  }

  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) {
      return null;
    }

    const { startDate, endDate } = JSON.parse(raw);
    const parsedStart = new Date(startDate);
    const parsedEnd = new Date(endDate);

    if (Number.isNaN(parsedStart.getTime()) || Number.isNaN(parsedEnd.getTime())) {
      return null;
    }

    return [buildSelectionRange(parsedStart, parsedEnd)];
  } catch {
    return null;
  }
};

const saveSectionDateRangeToSession = (personId, section, startDate, endDate) => {
  const key = getSessionStorageKey(personId, section);
  if (!key || !startDate || !endDate) {
    return;
  }

  sessionStorage.setItem(key, JSON.stringify({
    startDate: format(startDate, 'yyyy-MM-dd'),
    endDate: format(endDate, 'yyyy-MM-dd'),
  }));
};

const clearEmployeeDetailDateFiltersFromSession = (personId) => {
  EMPLOYEE_DETAIL_DATE_SECTIONS.forEach((section) => {
    const key = getSessionStorageKey(personId, section);
    if (key) {
      sessionStorage.removeItem(key);
    }
  });
};

const updateDateFiltersInUrl = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return;
  }

  const url = new URL(window.location.href);
  url.searchParams.set('start_date', format(startDate, 'yyyy-MM-dd'));
  url.searchParams.set('end_date', format(endDate, 'yyyy-MM-dd'));
  window.history.replaceState({}, '', url);
};

const clearDateFiltersFromUrl = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete('start_date');
  url.searchParams.delete('end_date');
  window.history.replaceState({}, '', url);
};

const getInitialEmployeeDetailDateFilters = (personId, search) => {
  const today = new Date();
  const params = new URLSearchParams(search || '');
  const logsFromSession = loadSectionDateRangeFromSession(personId, 'logs');

  return {
    attendance: loadSectionDateRangeFromSession(personId, 'attendance')
      || getDefaultDateRangeForSection('attendance'),
    activity: loadSectionDateRangeFromSession(personId, 'activity')
      || getDefaultDateRangeForSection('activity'),
    logs: logsFromSession
      || (searchHasDateParams(search)
        ? getDateRangeFromSearch(search, today, today)
        : getDefaultDateRangeForSection('logs')),
    activityLogs: loadSectionDateRangeFromSession(personId, 'activity_logs')
      || getDefaultDateRangeForSection('activity_logs'),
    camera: params.get('logs_camera') || '',
  };
};

// Shimmer animation for skeleton loading
const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const shimmerStyles = {
  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
  backgroundSize: '1000px 100%',
  animation: `${shimmer} 1.5s ease-in-out infinite`,
};

// Skeleton component for logs tiles (In/Out logs - 3 rows)
const LogsTileSkeleton = () => (
  <Card sx={{ width: '100%', bgcolor: '#fff', border: '1px solid', borderColor: 'divider' }}>
    {/* Image skeleton - aspect ratio container */}
    <Box sx={{ width: '100%', paddingTop: '75%', position: 'relative' }}>
      <Box sx={{ position: 'absolute', inset: 0, ...shimmerStyles }} />
    </Box>
    {/* Content skeleton with multiple lines */}
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Skeleton variant="text" sx={{ fontSize: '1rem', width: '70%' }} />
        {/* Skeleton for numbered indicator badge */}
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '50%',
            border: '1px solid rgba(0,0,0,0.2)',
            ...shimmerStyles,
          }}
        />
      </Box>
      <Skeleton variant="text" sx={{ fontSize: '0.875rem', mb: 1 }} />
      <Skeleton variant="text" sx={{ fontSize: '0.875rem', width: '60%' }} />
    </Box>
  </Card>
);

// Skeleton component for activity logs tiles (4 rows)
const ActivityLogsTileSkeleton = () => (
  <Card sx={{ width: '100%', bgcolor: '#fff', border: '1px solid', borderColor: 'divider' }}>
    {/* Image skeleton - aspect ratio container */}
    <Box sx={{ width: '100%', paddingTop: '75%', position: 'relative' }}>
      <Box sx={{ position: 'absolute', inset: 0, ...shimmerStyles }} />
    </Box>
    {/* Content skeleton with multiple lines */}
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Skeleton variant="text" sx={{ fontSize: '1rem', width: '70%' }} />
        {/* Skeleton for numbered indicator badge */}
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '50%',
            border: '1px solid rgba(0,0,0,0.2)',
            ...shimmerStyles,
          }}
        />
      </Box>
      <Skeleton variant="text" sx={{ fontSize: '0.875rem', mb: 1 }} />
      <Skeleton variant="text" sx={{ fontSize: '0.875rem', mb: 1 }} />
      <Skeleton variant="text" sx={{ fontSize: '0.875rem', width: '60%' }} />
    </Box>
  </Card>
);

// Skeleton component for Attendance timeline
const AttendanceTimelineSkeleton = () => (
  <Box>
    {/* Generate 3-5 day skeletons */}
    {Array.from({ length: 4 }).map((_, dayIndex) => (
      <Box key={`day-skel-${dayIndex}`} sx={{ mb: 3 }}>
        {/* Date header skeleton */}
        <Skeleton
          variant="text"
          sx={{
            fontSize: '0.9rem',
            fontWeight: 700,
            mb: 1.25,
            width: 180,
            height: 28
          }}
        />

        {/* Timeline bar skeleton */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Time markers skeleton */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 60 }}>
            {Array.from({ length: 3 }).map((_, timeIndex) => (
              <Skeleton
                key={`time-skel-${dayIndex}-${timeIndex}`}
                variant="text"
                sx={{ fontSize: '0.75rem', width: 45, height: 16 }}
              />
            ))}
          </Box>

          {/* Timeline content skeleton */}
          <Box sx={{ flex: 1, position: 'relative' }}>
            {/* Main timeline bar */}
            <Box sx={{
              height: 40,
              borderRadius: 2,
              mb: 1,
              ...shimmerStyles
            }} />

            {/* Event markers on timeline */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {Array.from({ length: 4 }).map((_, eventIndex) => (
                <Box
                  key={`event-skel-${dayIndex}-${eventIndex}`}
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    ...shimmerStyles
                  }}
                />
              ))}
            </Box>

            {/* Additional timeline details */}
            <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
              <Skeleton variant="text" sx={{ fontSize: '0.75rem', width: 80 }} />
              <Skeleton variant="text" sx={{ fontSize: '0.75rem', width: 60 }} />
            </Box>
          </Box>
        </Box>
      </Box>
    ))}
  </Box>
);

// Skeleton component for Activity timeline
const ActivityTimelineSkeleton = () => (
  <Box>
    {/* Generate 3-5 day skeletons */}
    {Array.from({ length: 4 }).map((_, dayIndex) => (
      <Box key={`activity-day-skel-${dayIndex}`} sx={{ mb: 3 }}>
        {/* Date header skeleton */}
        <Skeleton
          variant="text"
          sx={{
            fontSize: '0.9rem',
            fontWeight: 700,
            mb: 1.25,
            width: 180,
            height: 28
          }}
        />

        {/* Activity timeline skeleton */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Time markers skeleton */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 60 }}>
            {Array.from({ length: 3 }).map((_, timeIndex) => (
              <Skeleton
                key={`activity-time-skel-${dayIndex}-${timeIndex}`}
                variant="text"
                sx={{ fontSize: '0.75rem', width: 45, height: 16 }}
              />
            ))}
          </Box>

          {/* Activity timeline content skeleton */}
          <Box sx={{ flex: 1, position: 'relative' }}>
            {/* Multiple activity bars */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {Array.from({ length: 2 }).map((_, barIndex) => (
                <Box key={`activity-bar-skel-${dayIndex}-${barIndex}`}>
                  {/* Activity timeline bar */}
                  <Box sx={{
                    height: 32,
                    borderRadius: 2,
                    mb: 0.5,
                    ...shimmerStyles
                  }} />

                  {/* Activity event markers */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                    {Array.from({ length: 3 }).map((_, eventIndex) => (
                      <Box
                        key={`activity-event-skel-${dayIndex}-${barIndex}-${eventIndex}`}
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          ...shimmerStyles
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Activity details */}
            <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
              <Skeleton variant="text" sx={{ fontSize: '0.75rem', width: 90 }} />
              <Skeleton variant="text" sx={{ fontSize: '0.75rem', width: 70 }} />
              <Skeleton variant="text" sx={{ fontSize: '0.75rem', width: 50 }} />
            </Box>
          </Box>
        </Box>
      </Box>
    ))}
  </Box>
);

const resolveTimestamp = (isoValue, msValue, fallbackIso) => {
  let resolvedIso = isoValue || '';
  let resolvedMs = normaliseMs(msValue);
  if (!resolvedIso) {
    const fromMs = msToIsoString(resolvedMs);
    if (fromMs) resolvedIso = fromMs;
  }
  if (!resolvedIso && fallbackIso) {
    resolvedIso = fallbackIso;
  }
  if ((resolvedMs === null || resolvedMs === undefined) && resolvedIso) {
    const parsed = parseIsoToEpochMs(resolvedIso);
    if (!Number.isNaN(parsed)) {
      resolvedMs = parsed;
    }
  }
  if (resolvedMs !== null && resolvedMs !== undefined) {
    const canonicalIso = msToIsoString(resolvedMs);
    if (canonicalIso) {
      resolvedIso = canonicalIso;
    }
  }
  return { iso: resolvedIso, ms: resolvedMs };
};

const getStackLogKey = (log) => {
  if (!log) return null;
  if (log.audit_id) return `audit:${String(log.audit_id)}`;
  const timestamp = log.captured_at || log.created_at || '';
  const camera = (log.camera || '').toString().toLowerCase();
  return `fallback:${String(log.track_id || 'track')}:${camera}:${String(timestamp)}`;
};

const buildPrioritizedStackImages = (bucketLogs, representativeLog, imageCache) => {
  const logs = Array.isArray(bucketLogs) ? bucketLogs : [];
  const orderedLogs = [];
  const seen = new Map();

  const addLog = (log) => {
    const key = getStackLogKey(log);
    if (!key) return;
    if (seen.has(key)) {
      const existingIndex = seen.get(key);
      orderedLogs[existingIndex] = {
        ...orderedLogs[existingIndex],
        ...log,
      };
      return;
    }
    seen.set(key, orderedLogs.length);
    orderedLogs.push(log);
  };

  addLog(representativeLog);
  logs.forEach(addLog);

  return orderedLogs.slice(0, MAX_STACK_PREVIEW).map((stackLog) => ({
    log: stackLog,
    image: stackLog.audit_id ? imageCache[stackLog.audit_id] : null,
  }));
};

const getCapturedTimestamp = (log) => {
  const meta = log.request_metadata || {};
  return resolveTimestamp(
    log.captured_at || meta.captured_at,
    log.captured_at_ms ?? meta.captured_at_ms,
    log.created_at
  );
};

const getRequestSentTimestamp = (log) => {
  const meta = log.request_metadata || {};
  return resolveTimestamp(
    log.recognize_started_at ?? meta.recognize_started_at,
    log.recognize_started_at_ms ?? meta.recognize_started_at_ms ?? null,
    null
  );
};

const getApiSubmitTimestamp = (log) => {
  const meta = log.request_metadata || {};
  return resolveTimestamp(
    log.api_submit_at || meta.api_submit_at,
    log.api_submit_at_ms ?? meta.api_submit_at_ms,
    log.created_at
  );
};

const getApiReceivedTimestamp = (log) => {
  const meta = log.request_metadata || {};
  return resolveTimestamp(
    log.api_received_at ?? meta.api_received_at ?? null,
    log.api_received_at_ms ?? meta.api_received_at_ms ?? null,
    null
  );
};

const EmployeeDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openImagePreview } = useImagePreview();
  const personIdFromPath = location.pathname.split('/').pop();
  const previousPersonIdRef = useRef(null);
  const initialDateFiltersRef = useRef(null);
  if (!initialDateFiltersRef.current) {
    initialDateFiltersRef.current = getInitialEmployeeDetailDateFilters(personIdFromPath, location.search);
  }
  const initialDateFilters = initialDateFiltersRef.current;
  const detailTitle = 'Employee Details';
  const detailUnavailableMessage = 'Employee data not available';
  const detailBackPath = '/employees';
  const detailBackLabel = 'Back to Employees';
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [faceImages, setFaceImages] = useState({});

  // Date Range Picker state for Attendance (default to last 7 days)
  const today = new Date();
  const sixDaysAgo = new Date(today);
  sixDaysAgo.setDate(today.getDate() - 6);
  const [aDateRange, setADateRange] = useState(() => initialDateFilters.attendance);
  const [aDatePickerAnchor, setADatePickerAnchor] = useState(null);
  const aDatePickerOpen = Boolean(aDatePickerAnchor);
  const [aStartTime, setAStartTime] = useState('00:00');
  const [aEndTime, setAEndTime] = useState('23:59');
  const [aDateDraftRange, setADateDraftRange] = useState(() => initialDateFilters.attendance);
  const [aStartTimeDraft, setAStartTimeDraft] = useState('00:00');
  const [aEndTimeDraft, setAEndTimeDraft] = useState('23:59');

  // Export dialog state
  const [attendanceExportDialogOpen, setAttendanceExportDialogOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [attendanceExportFormat, setAttendanceExportFormat] = useState('csv');
  const [attendanceExportMenuAnchor, setAttendanceExportMenuAnchor] = useState(null);
  const attendanceExportMenuOpen = Boolean(attendanceExportMenuAnchor);
  const [attendanceExportFields, setAttendanceExportFields] = useState([]);
  const [attendanceExportSelectedFields, setAttendanceExportSelectedFields] = useState([]);
  const [attendanceExportFileName, setAttendanceExportFileName] = useState('');
  const [attendanceExportDateRange, setAttendanceExportDateRange] = useState([
    { startDate: sixDaysAgo, endDate: today, key: 'selection' }
  ]);
  const [attendanceExportStartTime, setAttendanceExportStartTime] = useState('00:00');
  const [attendanceExportEndTime, setAttendanceExportEndTime] = useState('23:59');
  const [attendanceExportDatePickerAnchor, setAttendanceExportDatePickerAnchor] = useState(null);
  const attendanceExportDatePickerOpen = Boolean(attendanceExportDatePickerAnchor);
  const [attendanceExportPersonName, setAttendanceExportPersonName] = useState('');
  const [attendanceExportAllEmployeesPresence, setAttendanceExportAllEmployeesPresence] = useState(false);
  const [attendanceExportAction, setAttendanceExportAction] = useState('');
  const [attendanceExportPremisesFilter, setAttendanceExportPremisesFilter] = useState('');
  const [attendanceExportLateFilter, setAttendanceExportLateFilter] = useState(false);

  // Accordion expansion state - persisted in localStorage
  const [expandedAccordion, setExpandedAccordion] = useState(() => {
    const savedAccordion = localStorage.getItem('employeeDetailsExpandedAccordion');
    return savedAccordion || 'basic-details';
  });

  // Refs for scrolling to sections
  const basicDetailsRef = useRef(null);
  const attendanceRef = useRef(null);
  const activityRef = useRef(null);
  const logsRef = useRef(null);
  const activityLogsRef = useRef(null);

  // Save expandedAccordion to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('employeeDetailsExpandedAccordion', expandedAccordion);
  }, [expandedAccordion]);

  // Reset accordion and date filters when navigating away (not on page refresh)
  const clearSessionOnLeaveRef = useRef(true);
  useEffect(() => {
    const personId = personIdFromPath;

    const handleBeforeUnload = () => {
      clearSessionOnLeaveRef.current = false;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (clearSessionOnLeaveRef.current) {
        clearEmployeeDetailDateFiltersFromSession(personId);
      }
      localStorage.setItem('employeeDetailsExpandedAccordion', 'basic-details');
    };
  }, [personIdFromPath]);

  // Attendance data state
  const [attendanceDayGroups, setAttendanceDayGroups] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceError, setAttendanceError] = useState(null);
  const [attendancePagination, setAttendancePagination] = useState({
    limit: 10,
    offset: 0,
    current_page: 1,
    total: 0,
  });
  const [attendanceTotalEvents, setAttendanceTotalEvents] = useState(0);

  const attendancePageEvents = useMemo(
    () => attendanceDayGroups.flatMap((day) => (Array.isArray(day.events) ? day.events : [])),
    [attendanceDayGroups]
  );
  const [attendanceViewMode, setAttendanceViewMode] = useState('timeline');
  const [attendanceGraphPeriodDays, setAttendanceGraphPeriodDays] = useState(7);
  const [attendanceGraphData, setAttendanceGraphData] = useState([]);
  const [attendanceGraphLoading, setAttendanceGraphLoading] = useState(false);
  const [attendanceGraphError, setAttendanceGraphError] = useState(null);
  const [attendanceGraphDateRange, setAttendanceGraphDateRange] = useState(() => {
    const endDate = new Date();
    endDate.setHours(0, 0, 0, 0);
    return {
      startDate: addDays(endDate, -6),
      endDate,
    };
  });

  // Activity data state
  const [activityData, setActivityData] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(null);
  const [activityExportLoading, setActivityExportLoading] = useState(false);
  const [activityExportMenuAnchor, setActivityExportMenuAnchor] = useState(null);
  const activityExportMenuOpen = Boolean(activityExportMenuAnchor);

  // Logs data state
  const [logsData, setLogsData] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState(null);
  const [timingDialog, setTimingDialog] = useState({ open: false, log: null, stack: [], detailLoading: false }); // For modal
  const [imageCache, setImageCache] = useState({}); // For log images
  const [attendanceSnapshots, setAttendanceSnapshots] = useState({}); // For attendance timeline images
  const [activityImageUrls, setActivityImageUrls] = useState({}); // For activity timeline images
  const [logsPagination, setLogsPagination] = useState({ limit: 100, offset: 0, current_page: 1, total: 0 }); // Pagination state
  const [logsExportLoading, setLogsExportLoading] = useState(false); // Export loading state
  const [logsExportMenuAnchor, setLogsExportMenuAnchor] = useState(null);
  const logsExportMenuOpen = Boolean(logsExportMenuAnchor);

  // Activity logs state for Logs section
  const [activityLogsData, setActivityLogsData] = useState([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  const [activityLogsError, setActivityLogsError] = useState(null);
  const [activityLogsPagination, setActivityLogsPagination] = useState({ limit: 100, offset: 0, current_page: 1, total: 0 });
  const [activityLogsCameraId, setActivityLogsCameraId] = useState('');
  const [activityLogsCameras, setActivityLogsCameras] = useState([]);
  const [activityLogsDateRange, setActivityLogsDateRange] = useState(() => initialDateFilters.activityLogs);
  const [activityLogsDraftDateRange, setActivityLogsDraftDateRange] = useState(() => initialDateFilters.activityLogs);
  const [activityLogsStartTime, setActivityLogsStartTime] = useState('00:00');
  const [activityLogsEndTime, setActivityLogsEndTime] = useState('23:59');
  const [activityLogsStartTimeDraft, setActivityLogsStartTimeDraft] = useState('00:00');
  const [activityLogsEndTimeDraft, setActivityLogsEndTimeDraft] = useState('23:59');
  const [activityLogsDatePickerAnchor, setActivityLogsDatePickerAnchor] = useState(null);

  // Selected face for large display
  const [selectedFace, setSelectedFace] = useState(null);

  // Activity date filter state
  const [activityDateRange, setActivityDateRange] = useState(() => initialDateFilters.activity);
  const [activityDraftDateRange, setActivityDraftDateRange] = useState(() => initialDateFilters.activity);
  const [activityStartTime, setActivityStartTime] = useState('00:00');
  const [activityEndTime, setActivityEndTime] = useState('23:59');
  const [activityStartTimeDraft, setActivityStartTimeDraft] = useState('00:00');
  const [activityEndTimeDraft, setActivityEndTimeDraft] = useState('23:59');
  const [activityDatePickerAnchor, setActivityDatePickerAnchor] = useState(null);
  const activityDatePickerOpen = Boolean(activityDatePickerAnchor);

  // Date Range Picker state for Logs (default to today)
  const [logsDateRange, setLogsDateRange] = useState(() => initialDateFilters.logs);
  const [logsDateDraftRange, setLogsDateDraftRange] = useState(() => initialDateFilters.logs);
  const [logsDatePickerAnchor, setLogsDatePickerAnchor] = useState(null);
  const logsDatePickerOpen = Boolean(logsDatePickerAnchor);
  // Time filter state for Logs
  const [logsStartTime, setLogsStartTime] = useState('00:00');
  const [logsEndTime, setLogsEndTime] = useState('23:59');
  const [logsStartTimeDraft, setLogsStartTimeDraft] = useState('00:00');
  const [logsEndTimeDraft, setLogsEndTimeDraft] = useState('23:59');

  // Camera filter state for Logs
  const [camera, setCamera] = useState(() => initialDateFilters.camera);
  const hasHydratedUrlFiltersRef = useRef(false);

  // Get employee data from location state or fetch it
  useEffect(() => {
    const fetchEmployeeData = async () => {
      if (location.state?.person) {
        setEmployee(location.state.person);
        setLoading(false);
      } else {
        // If no state data, fetch from API using the person_id from URL
        const personId = window.location.pathname.split('/').pop();
        if (personId && personId !== 'employee-details') {
          try {
            setLoading(true);
            const person = await getPersonById(personId);
            if (person) {
              setEmployee(person);
              setLoading(false);
            } else {
              setError(detailUnavailableMessage);
              setLoading(false);
            }
          } catch (err) {
            console.error('Error fetching employee data:', err);
            setError(detailUnavailableMessage);
            setLoading(false);
          }
        } else {
          setError(detailUnavailableMessage);
          setLoading(false);
        }
      }
    };

    fetchEmployeeData();
  }, [location.state, detailUnavailableMessage]);

  const fetchFaceImages = useCallback(async (personId, faces) => {
    try {
      if (!faces || faces.length === 0) {
        setFaceImages({});
        return;
      }

      // Fetch all face images for the employee
      const imagePromises = faces
        .filter(face => !face.deleted_at)
        .map(async (face) => {
          try {
            const blob = await getFaceImage(face.face_id);
            const imageUrl = URL.createObjectURL(blob);
            return { faceId: face.face_id, imageUrl };
          } catch (err) {
            console.error('Error fetching face image for:', face.face_id, err);
            return null;
          }
        });

      const results = await Promise.all(imagePromises);
      const images = {};
      results.forEach(result => {
        if (result) {
          images[result.faceId] = result.imageUrl;
        }
      });

      setFaceImages(images);
    } catch (err) {
      console.error('Error fetching face images:', err);
      setFaceImages({});
    }
  }, []);

  const handleViewFaceImage = async (face) => {
    if (!face?.face_id) return;

    try {
      setSelectedFace(face);

      const faces = Array.isArray(employee?.faces)
        ? employee.faces.filter((item) => !item.deleted_at)
        : [];
      const imageMap = { ...faceImages };

      const missingFaces = faces.filter((item) => !imageMap[item.face_id]);
      if (missingFaces.length > 0) {
        const loaded = await Promise.all(
          missingFaces.map(async (item) => {
            try {
              const imageBlob = await getFaceImage(item.face_id);
              return { faceId: item.face_id, imageUrl: URL.createObjectURL(imageBlob) };
            } catch (err) {
              console.error('Error fetching face image for:', item.face_id, err);
              return null;
            }
          })
        );
        const newlyLoaded = {};
        loaded.forEach((result) => {
          if (!result) return;
          imageMap[result.faceId] = result.imageUrl;
          newlyLoaded[result.faceId] = result.imageUrl;
        });
        if (Object.keys(newlyLoaded).length > 0) {
          setFaceImages((prev) => ({ ...prev, ...newlyLoaded }));
        }
      }

      const galleryImages = faces
        .map((item) => {
          const src = imageMap[item.face_id];
          if (!src) return null;
          const label = item.face_angle || item.name || 'Face';
          return {
            src,
            title: label,
            alt: `Face - ${label}`,
          };
        })
        .filter(Boolean);

      if (galleryImages.length === 0) return;

      const clickedSrc = imageMap[face.face_id];
      if (!clickedSrc) return;

      const index = Math.max(0, galleryImages.findIndex((item) => item.src === clickedSrc));
      const frontFace = faces.find((item) => item.face_angle === 'front');
      const profileImage = (frontFace && imageMap[frontFace.face_id])
        || galleryImages[0]?.src
        || '';

      openImagePreview({
        src: clickedSrc,
        title: face.face_angle || face.name || 'Face Image',
        alt: `Face - ${face.face_angle || face.name || 'unknown'}`,
        images: galleryImages,
        index,
        employeeName: employee?.name || 'Unknown person',
        employeeId: employee?.employee_code || employee?.person_id || '',
        employeeImage: profileImage,
      });
    } catch (error) {
      console.error('Error loading face image:', error);
    }
  };

  // Fetch face images when employee is loaded
  useEffect(() => {
    if (employee?.person_id && employee?.faces && expandedAccordion === 'basic-details') {
      fetchFaceImages(employee.person_id, employee.faces);
      // Set front face as default selected face
      const frontFace = employee.faces.find(face => !face.deleted_at && face.face_angle === 'front');
      if (frontFace) {
        setSelectedFace(frontFace);
      } else {
        const firstFace = employee.faces.find(face => !face.deleted_at);
        setSelectedFace(firstFace);
      }
    }
  }, [employee?.person_id, employee?.faces, fetchFaceImages, expandedAccordion]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // CSV export helper functions
  const csvEscapeValue = (value) => {
    if (value === null || value === undefined) return '';
    const stringValue = String(value);
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const formatExportValue = (field, value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.map((item) => formatExportValue(field, item)).join(' | ');
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    // Only format specific timestamp fields
    const timestampFields = ['created_at', 'updated_at', 'captured_at', 'timestamp', 'time', 'date'];
    if (timestampFields.includes(field) || field.endsWith('_at') || field.endsWith('_time') || field.endsWith('_date')) {
      return formatDateWithSeconds(value);
    }
    return String(value).trim();
  };

  const convertJsonToCsv = (data, fieldsOverride = null, headersOverride = null) => {
    const fields = fieldsOverride || data?.fields;
    const headers = headersOverride || data?.headers;
    if (!data || !fields || !headers || fields.length !== headers.length) {
      throw new Error('Invalid export payload');
    }

    const logs = data.logs || [];

    // Create CSV header row using the provided headers
    const csvRows = [];
    csvRows.push(headers.map((header) => csvEscapeValue(header)).join(','));

    // Create CSV data rows (may be empty)
    for (const log of logs) {
      const values = fields.map(field => {
        const value = formatExportValue(field, log?.[field]);
        return csvEscapeValue(value);
      });
      csvRows.push(values.join(','));
    }

    return csvRows.join('\n');
  };

  const downloadAttendanceXlsxFile = (
    exportPayload,
    fields = [],
    headers = [],
    fileName = 'attendance_logs',
    sheetName = 'Attendance Logs',
  ) => {
    if (!exportPayload || !Array.isArray(fields) || !Array.isArray(headers) || fields.length !== headers.length || !fields.length) {
      throw new Error('Invalid XLSX export payload');
    }
    const logs = Array.isArray(exportPayload?.logs) ? exportPayload.logs : [];
    const worksheetRows = [
      headers,
      ...logs.map((log) => fields.map((field) => formatExportValue(field, log?.[field]))),
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName || 'Sheet1');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const safeFileName = (fileName || 'attendance_logs')
      .trim()
      .replace(/[^a-zA-Z0-9_-]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '') || 'attendance_logs';
    XLSX.writeFile(workbook, `${safeFileName}_${timestamp}.xlsx`);
  };

  const handleLogsExport = async (formatType = 'csv') => {
    try {
      setLogsExportMenuAnchor(null);
      setLogsExportLoading(true);

      const { formattedStart, formattedEnd } = buildLogsDateTimeRange();

      const jsonData = await exportAuditLogs({
        person_id: employee?.person_id,
        start_time: formattedStart,
        end_time: formattedEnd,
        camera: camera,
      });

      const normalizedFormat = String(formatType || 'csv').toLowerCase() === 'xlsx' ? 'xlsx' : 'csv';
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const baseFileName = (employee?.name ? `in_out_logs_${employee.name}` : 'in_out_logs')
        .trim()
        .replace(/[^a-zA-Z0-9_-]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '') || 'in_out_logs';

      if (normalizedFormat === 'xlsx') {
        if (!Array.isArray(jsonData?.fields) || !Array.isArray(jsonData?.headers)) {
          throw new Error('Invalid export payload');
        }
        downloadAttendanceXlsxFile(jsonData, jsonData.fields, jsonData.headers, baseFileName, 'In-Out Logs');
      } else {
        const csvContent = convertJsonToCsv(jsonData);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${baseFileName}_${timestamp}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }

      console.log(`Audit logs exported successfully as ${normalizedFormat.toUpperCase()} (${jsonData.total_records} records)`);
    } catch (e) {
      console.error('Export error:', e);
      console.error('Failed to export audit logs');
    } finally {
      setLogsExportLoading(false);
    }
  };

  const openLogsExportMenu = (event) => {
    setLogsExportMenuAnchor(event.currentTarget);
  };

  const closeLogsExportMenu = () => {
    setLogsExportMenuAnchor(null);
  };

  const handleChooseLogsExportFormat = (format) => {
    closeLogsExportMenu();
    handleLogsExport(format);
  };

  const quickNavigationItems = [
    { id: 'basic-details', label: 'Basic Details', icon: Person },
    { id: 'attendance', label: 'Attendance', icon: CalendarToday },
    { id: 'activity', label: 'Activity', icon: DirectionsRunIcon },
    { id: 'logs-inout', label: 'In/Out Logs', icon: AssessmentIcon },
    { id: 'logs-activity', label: 'Activity Logs', icon: DirectionsRunIcon },
  ];
  const formatQuickNavLabel = (label = '') =>
    String(label)
      .toLowerCase()
      .replace(/\b[a-z]/g, (char) => char.toUpperCase());

  const activeFaces = Array.isArray(employee?.faces)
    ? employee.faces.filter((face) => !face.deleted_at)
    : [];

  const frontFace = activeFaces.find(
    (face) => String(face.face_angle || "").toLowerCase() === "front"
  );

  const displayFace =
    selectedFace && activeFaces.some(
      (face) => face.face_id === selectedFace.face_id
    )
      ? selectedFace
      : (frontFace || activeFaces[0] || null);

  const displayFaceImage = displayFace?.face_id
    ? faceImages[displayFace.face_id]
    : null;


  const handleAccordionNavigation = (accordionId) => {
    setExpandedAccordion(accordionId);
  };

  const openTimingDialog = async ({ log, stack = [] }) => {
    const auditId = log?.audit_id;
    setTimingDialog({ open: true, log, stack, detailLoading: Boolean(auditId) });
    if (!auditId) return;
    try {
      const next = await hydrateTimingDialogFromDetail(log, stack);
      setTimingDialog((prev) => {
        if (!prev.open || String(prev.log?.audit_id || '') !== String(auditId)) return prev;
        return { open: true, ...next };
      });
    } catch (e) {
      console.error('Failed to load audit log detail:', e);
      setTimingDialog((prev) => (
        prev.open && String(prev.log?.audit_id || '') === String(auditId)
          ? { ...prev, detailLoading: false }
          : prev
      ));
    }
  };

  const closeTimingDialog = () => {
    setTimingDialog({ open: false, log: null, stack: [], detailLoading: false });
  };

  const buildAttendanceDateTimeRange = useCallback(() => {
    const startDate = aDateRange[0]?.startDate || new Date();
    const endDate = aDateRange[0]?.endDate || startDate;
    const parseTime = (timeValue, fallbackHour, fallbackMinute) => {
      if (!timeValue || typeof timeValue !== 'string' || !timeValue.includes(':')) {
        return [fallbackHour, fallbackMinute];
      }
      const [hoursRaw, minutesRaw] = timeValue.split(':');
      const hours = Number(hoursRaw);
      const minutes = Number(minutesRaw);
      if (
        Number.isNaN(hours) ||
        Number.isNaN(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59
      ) {
        return [fallbackHour, fallbackMinute];
      }
      return [hours, minutes];
    };

    // Parse IST times (user input)
    const [istStartHour, istStartMinute] = parseTime(aStartTime, 0, 0);
    const [istEndHour, istEndMinute] = parseTime(aEndTime, 23, 59);

    // IST is UTC+5:30, so we need to subtract 5:30 from IST time to get UTC
    // Create date objects representing IST time, then convert to UTC
    const startDateIST = new Date(Date.UTC(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      istStartHour,
      istStartMinute,
      0
    ));
    // Subtract 5 hours 30 minutes (IST offset) to convert to UTC
    const startDateTimeUTC = new Date(startDateIST.getTime() - (5 * 60 + 30) * 60 * 1000);

    const endDateIST = new Date(Date.UTC(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
      istEndHour,
      istEndMinute,
      0
    ));
    // Subtract 5 hours 30 minutes (IST offset) to convert to UTC
    const endDateTimeUTC = new Date(endDateIST.getTime() - (5 * 60 + 30) * 60 * 1000);

    const formattedStart = startDateTimeUTC.toISOString().slice(0, 16); // yyyy-MM-ddTHH:mm
    const formattedEnd = endDateTimeUTC.toISOString().slice(0, 16); // yyyy-MM-ddTHH:mm

    return { formattedStart, formattedEnd };
  }, [aDateRange, aStartTime, aEndTime]);

  const buildGraphDateTimeRange = useCallback(() => {
    const { startDate, endDate } = attendanceGraphDateRange;

    const startDateIST = new Date(Date.UTC(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      0,
      0,
      0
    ));
    const startDateTimeUTC = new Date(startDateIST.getTime() - (5 * 60 + 30) * 60 * 1000);

    const endDateIST = new Date(Date.UTC(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
      23,
      59,
      0
    ));
    const endDateTimeUTC = new Date(endDateIST.getTime() - (5 * 60 + 30) * 60 * 1000);

    return {
      formattedStart: startDateTimeUTC.toISOString().slice(0, 16),
      formattedEnd: endDateTimeUTC.toISOString().slice(0, 16),
    };
  }, [attendanceGraphDateRange]);

  const handleGraphPeriodChange = useCallback((days) => {
    const endDate = new Date();
    endDate.setHours(0, 0, 0, 0);
    const startDate = addDays(endDate, -(days - 1));
    setAttendanceGraphPeriodDays(days);
    setAttendanceGraphDateRange({ startDate, endDate });
  }, []);

  const handleGraphCustomDateRangeApply = useCallback(({ startDate, endDate }) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    setAttendanceGraphPeriodDays(null);
    setAttendanceGraphDateRange({ startDate: start, endDate: end });
  }, []);

  const handleOpenAttendanceDatePicker = useCallback((event) => {
    const startDate = aDateRange[0]?.startDate ? new Date(aDateRange[0].startDate) : new Date();
    const endDate = aDateRange[0]?.endDate ? new Date(aDateRange[0].endDate) : startDate;
    setADateDraftRange([{ startDate, endDate, key: 'selection' }]);
    setAStartTimeDraft(aStartTime);
    setAEndTimeDraft(aEndTime);
    setADatePickerAnchor(event.currentTarget);
  }, [aDateRange, aStartTime, aEndTime]);

  const handleApplyAttendanceDateFilters = useCallback(() => {
    setADateRange(aDateDraftRange);
    setAStartTime(aStartTimeDraft);
    setAEndTime(aEndTimeDraft);
    setADatePickerAnchor(null);
    saveSectionDateRangeToSession(
      personIdFromPath,
      'attendance',
      aDateDraftRange[0]?.startDate,
      aDateDraftRange[0]?.endDate
    );
  }, [aDateDraftRange, aStartTimeDraft, aEndTimeDraft, personIdFromPath]);

  const handleResetAttendanceDateFilters = useCallback(() => {
    const today = new Date();
    setADateDraftRange([{ startDate: today, endDate: today, key: 'selection' }]);
    setAStartTimeDraft('00:00');
    setAEndTimeDraft('23:59');
  }, []);

  const handleOpenActivityDatePicker = useCallback((event) => {
    const startDate = activityDateRange[0]?.startDate ? new Date(activityDateRange[0].startDate) : new Date();
    const endDate = activityDateRange[0]?.endDate ? new Date(activityDateRange[0].endDate) : startDate;
    setActivityDraftDateRange([{ startDate, endDate, key: 'selection' }]);
    setActivityStartTimeDraft(activityStartTime);
    setActivityEndTimeDraft(activityEndTime);
    setActivityDatePickerAnchor(event.currentTarget);
  }, [activityDateRange, activityStartTime, activityEndTime]);

  const handleResetActivityDateFilters = useCallback(() => {
    const today = new Date();
    setActivityDraftDateRange([{ startDate: today, endDate: today, key: 'selection' }]);
    setActivityStartTimeDraft('00:00');
    setActivityEndTimeDraft('23:59');
  }, []);

  const buildLogsDateTimeRange = useCallback((overrides = {}) => {
    const effectiveDateRange = Array.isArray(overrides.dateRange) && overrides.dateRange.length
      ? overrides.dateRange
      : logsDateRange;
    const effectiveStartTime = typeof overrides.startTime === 'string' ? overrides.startTime : logsStartTime;
    const effectiveEndTime = typeof overrides.endTime === 'string' ? overrides.endTime : logsEndTime;
    const startDate = effectiveDateRange[0]?.startDate || new Date();
    const endDate = effectiveDateRange[0]?.endDate || startDate;
    const parseTime = (timeValue, fallbackHour, fallbackMinute) => {
      if (!timeValue || typeof timeValue !== 'string' || !timeValue.includes(':')) {
        return [fallbackHour, fallbackMinute];
      }
      const [hoursRaw, minutesRaw] = timeValue.split(':');
      const hours = Number(hoursRaw);
      const minutes = Number(minutesRaw);
      if (
        Number.isNaN(hours) ||
        Number.isNaN(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59
      ) {
        return [fallbackHour, fallbackMinute];
      }
      return [hours, minutes];
    };

    // Parse IST times (user input)
    const [istStartHour, istStartMinute] = parseTime(effectiveStartTime, 0, 0);
    const [istEndHour, istEndMinute] = parseTime(effectiveEndTime, 23, 59);

    // IST is UTC+5:30, so we need to subtract 5:30 from IST time to get UTC
    // Create date objects representing IST time, then convert to UTC
    const startDateIST = new Date(Date.UTC(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      istStartHour,
      istStartMinute,
      0
    ));
    // Subtract 5 hours 30 minutes (IST offset) to convert to UTC
    const startDateTimeUTC = new Date(startDateIST.getTime() - (5 * 60 + 30) * 60 * 1000);

    const endDateIST = new Date(Date.UTC(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
      istEndHour,
      istEndMinute,
      0
    ));
    // Subtract 5 hours 30 minutes (IST offset) to convert to UTC
    const endDateTimeUTC = new Date(endDateIST.getTime() - (5 * 60 + 30) * 60 * 1000);

    const formattedStart = startDateTimeUTC.toISOString().slice(0, 16); // yyyy-MM-ddTHH:mm
    const formattedEnd = endDateTimeUTC.toISOString().slice(0, 16); // yyyy-MM-ddTHH:mm

    return { formattedStart, formattedEnd };
  }, [logsDateRange, logsStartTime, logsEndTime]);

  const buildActivityLogsDateTimeRange = useCallback((overrides = {}) => {
    const effectiveDateRange = Array.isArray(overrides.dateRange) && overrides.dateRange.length
      ? overrides.dateRange
      : activityLogsDateRange;
    const effectiveStartTime = typeof overrides.startTime === 'string' ? overrides.startTime : activityLogsStartTime;
    const effectiveEndTime = typeof overrides.endTime === 'string' ? overrides.endTime : activityLogsEndTime;
    const startDate = effectiveDateRange[0]?.startDate || new Date();
    const endDate = effectiveDateRange[0]?.endDate || startDate;
    const parseTime = (timeValue, fallbackHour, fallbackMinute) => {
      if (!timeValue || typeof timeValue !== 'string' || !timeValue.includes(':')) {
        return [fallbackHour, fallbackMinute];
      }
      const [hoursRaw, minutesRaw] = timeValue.split(':');
      const hours = Number(hoursRaw);
      const minutes = Number(minutesRaw);
      if (
        Number.isNaN(hours) ||
        Number.isNaN(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59
      ) {
        return [fallbackHour, fallbackMinute];
      }
      return [hours, minutes];
    };

    // Parse IST times (user input)
    const [istStartHour, istStartMinute] = parseTime(effectiveStartTime, 0, 0);
    const [istEndHour, istEndMinute] = parseTime(effectiveEndTime, 23, 59);

    // IST is UTC+5:30, so we need to subtract 5:30 from IST time to get UTC
    const startDateIST = new Date(Date.UTC(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      istStartHour,
      istStartMinute,
      0
    ));
    const startDateTimeUTC = new Date(startDateIST.getTime() - (5 * 60 + 30) * 60 * 1000);

    const endDateIST = new Date(Date.UTC(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
      istEndHour,
      istEndMinute,
      0
    ));
    const endDateTimeUTC = new Date(endDateIST.getTime() - (5 * 60 + 30) * 60 * 1000);

    const formattedStart = startDateTimeUTC.toISOString().slice(0, 16); // yyyy-MM-ddTHH:mm
    const formattedEnd = endDateTimeUTC.toISOString().slice(0, 16); // yyyy-MM-ddTHH:mm

    return { formattedStart, formattedEnd };
  }, [activityLogsDateRange, activityLogsStartTime, activityLogsEndTime]);

  const groupLogsByDate = useCallback((logs) => {
    const sourceLogs = Array.isArray(logs) ? logs : [];
    const groups = {};
    sourceLogs.forEach(log => {
      const ts = log.captured_at || log.created_at;
      const date = new Date(ts);
      const dateKey = date.toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric'
      });

      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(log);
    });
    return groups;
  }, []);

  // Fetch logs data
  const fetchLogs = useCallback(async (personId, limit = 100, offset = 0, rangeOverrides = {}) => {
    if (!personId) return;

    setLogsLoading(true);
    setLogsError(null);

    try {
      const { formattedStart, formattedEnd } = buildLogsDateTimeRange(rangeOverrides);

      const data = await getAuditLogs({
        person_id: personId,
        start_time: formattedStart,
        end_time: formattedEnd,
        camera: rangeOverrides.camera !== undefined ? rangeOverrides.camera : camera,
        limit: limit,
        offset: offset
      });

      setLogsData(data.logs || []);

      // Update pagination state
      const total = data.pagination?.total || data.total || 0;
      setLogsPagination({
        limit: limit,
        offset: offset,
        current_page: Math.floor(offset / limit) + 1,
        total: total
      });
    } catch (err) {
      console.error('Error fetching logs data:', err);
      setLogsError('Error fetching logs data');
    } finally {
      setLogsLoading(false);
    }
  }, [buildLogsDateTimeRange, camera]);

  // Load activity logs for Logs section
  const loadActivityLogs = useCallback(async (limit = 100, offset = 0, rangeOverrides = {}) => {
    if (!employee?.person_id) return;

    setActivityLogsLoading(true);
    setActivityLogsError(null);

    try {
      const { formattedStart, formattedEnd } = buildActivityLogsDateTimeRange(rangeOverrides);

      const data = await getActivityLogs({
        person_id: employee.person_id,
        start_time: formattedStart,
        end_time: formattedEnd,
        camera_id: (rangeOverrides.cameraId !== undefined ? rangeOverrides.cameraId : activityLogsCameraId) || undefined,
        limit,
        offset,
      });

      setActivityLogsData(data.logs || []);
      setActivityLogsPagination({
        limit: limit,
        offset: offset,
        current_page: Math.floor(offset / limit) + 1,
        total: data.pagination?.total || data.total || 0,
      });
    } catch (err) {
      console.error('Error fetching activity logs data:', err);
      setActivityLogsError('Error fetching activity logs data');
    } finally {
      setActivityLogsLoading(false);
    }
  }, [buildActivityLogsDateTimeRange, activityLogsCameraId, employee?.person_id]);

  // Load activity cameras
  useEffect(() => {
    if (expandedAccordion !== 'logs-activity') return;
    let cancelled = false;
    const run = async () => {
      try {
        const res = await getActivityCameras();
        if (!cancelled) {
          setActivityLogsCameras(Array.isArray(res?.cameras) ? res.cameras : []);
        }
      } catch (_) {
        if (!cancelled) setActivityLogsCameras([]);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [expandedAccordion]);

  // Load activity logs when expandedAccordion changes to logs-activity
  useEffect(() => {
    if (expandedAccordion === 'logs-activity' && employee?.person_id) {
      loadActivityLogs(activityLogsPagination.limit, 0);
    }
  }, [expandedAccordion, employee?.person_id, loadActivityLogs, activityLogsPagination.limit]);

  // Auto-refresh activity logs when camera selection changes
  useEffect(() => {
    if (expandedAccordion === 'logs-activity' && employee?.person_id) {
      setActivityLogsPagination(prev => ({ ...prev, offset: 0, current_page: 1 }));
      loadActivityLogs(activityLogsPagination.limit, 0);
    }
  }, [activityLogsCameraId]);

  // Activity logs helper functions
  const groupActivityLogsByDate = useCallback((logs) => {
    return (Array.isArray(logs) ? logs : []).reduce((acc, log) => {
      const date = log?.captured_at || log?.created_at;
      if (!date) return acc;
      const key = new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric'
      });
      if (!acc[key]) acc[key] = [];
      acc[key].push(log);
      return acc;
    }, {});
  }, []);

  const handleActivityLogsPageChange = useCallback((e, page) => {
    const newOffset = (page - 1) * activityLogsPagination.limit;
    loadActivityLogs(activityLogsPagination.limit, newOffset);
  }, [activityLogsPagination.limit, loadActivityLogs]);

  const handleOpenActivityLogsDatePicker = useCallback((event) => {
    const startDate = activityLogsDateRange[0]?.startDate ? new Date(activityLogsDateRange[0].startDate) : new Date();
    const endDate = activityLogsDateRange[0]?.endDate ? new Date(activityLogsDateRange[0].endDate) : startDate;
    setActivityLogsDraftDateRange([{ startDate, endDate, key: 'selection' }]);
    setActivityLogsStartTimeDraft(activityLogsStartTime);
    setActivityLogsEndTimeDraft(activityLogsEndTime);
    setActivityLogsDatePickerAnchor(event.currentTarget);
  }, [activityLogsDateRange, activityLogsStartTime, activityLogsEndTime]);

  const handleApplyActivityLogsDateFilters = useCallback(() => {
    const appliedDateRange = [{
      startDate: activityLogsDraftDateRange[0]?.startDate || activityLogsDateRange[0]?.startDate || new Date(),
      endDate: activityLogsDraftDateRange[0]?.endDate || activityLogsDraftDateRange[0]?.startDate || activityLogsDateRange[0]?.endDate || new Date(),
      key: 'selection',
    }];
    const appliedStartTime = activityLogsStartTimeDraft;
    const appliedEndTime = activityLogsEndTimeDraft;

    setActivityLogsDateRange(appliedDateRange);
    setActivityLogsStartTime(appliedStartTime);
    setActivityLogsEndTime(appliedEndTime);
    setActivityLogsPagination(prev => ({ ...prev, offset: 0, current_page: 1 }));
    setActivityLogsDatePickerAnchor(null);
    saveSectionDateRangeToSession(
      personIdFromPath,
      'activity_logs',
      appliedDateRange[0]?.startDate,
      appliedDateRange[0]?.endDate
    );
    loadActivityLogs(activityLogsPagination.limit, 0, {
      dateRange: appliedDateRange,
      startTime: appliedStartTime,
      endTime: appliedEndTime,
    });
  }, [activityLogsDraftDateRange, activityLogsStartTimeDraft, activityLogsEndTimeDraft, activityLogsDateRange, activityLogsPagination.limit, loadActivityLogs, personIdFromPath]);

  const handleResetActivityLogsDateFilters = useCallback(() => {
    const today = new Date();
    setActivityLogsDraftDateRange([{ startDate: today, endDate: today, key: 'selection' }]);
    setActivityLogsStartTimeDraft('00:00');
    setActivityLogsEndTimeDraft('23:59');
  }, []);

  const hasActiveInOutLogsFilters = useMemo(() => (
    !!camera || !isDefaultDateTimeRange(logsDateRange, logsStartTime, logsEndTime)
  ), [camera, logsDateRange, logsEndTime, logsStartTime]);

  const hasActiveActivityLogsFilters = useMemo(() => (
    !!activityLogsCameraId ||
    !isDefaultDateTimeRange(activityLogsDateRange, activityLogsStartTime, activityLogsEndTime)
  ), [activityLogsCameraId, activityLogsDateRange, activityLogsEndTime, activityLogsStartTime]);

  const handleClearInOutLogsFilters = useCallback(() => {
    setCamera('');
    const todayRange = createTodayDateRange();
    setLogsDateRange(todayRange);
    setLogsDateDraftRange(todayRange);
    setLogsStartTime(DEFAULT_START_TIME);
    setLogsEndTime(DEFAULT_END_TIME);
    setLogsStartTimeDraft(DEFAULT_START_TIME);
    setLogsEndTimeDraft(DEFAULT_END_TIME);
    setLogsPagination((prev) => ({ ...prev, offset: 0, current_page: 1 }));
    if (employee?.person_id) {
      fetchLogs(employee.person_id, logsPagination.limit, 0, {
        dateRange: todayRange,
        startTime: DEFAULT_START_TIME,
        endTime: DEFAULT_END_TIME,
        camera: '',
      });
    }
  }, [employee?.person_id, fetchLogs, logsPagination.limit]);

  const handleClearActivityLogsFilters = useCallback(() => {
    setActivityLogsCameraId('');
    const todayRange = createTodayDateRange();
    setActivityLogsDateRange(todayRange);
    setActivityLogsDraftDateRange(todayRange);
    setActivityLogsStartTime(DEFAULT_START_TIME);
    setActivityLogsEndTime(DEFAULT_END_TIME);
    setActivityLogsStartTimeDraft(DEFAULT_START_TIME);
    setActivityLogsEndTimeDraft(DEFAULT_END_TIME);
    setActivityLogsPagination((prev) => ({ ...prev, offset: 0, current_page: 1 }));
    loadActivityLogs(activityLogsPagination.limit, 0, {
      dateRange: todayRange,
      startTime: DEFAULT_START_TIME,
      endTime: DEFAULT_END_TIME,
      cameraId: '',
    });
  }, [activityLogsPagination.limit, loadActivityLogs]);

  // Handle image fetching for attendance timeline markers
  const handleRequestMarkerImage = useCallback(async (event) => {
    const auditId = event?.audit_id || event?.recognition_audit_log?.audit_id || event?.snapshot_audit_id;
    if (!auditId) return;
    const cached = peekAuditImage(auditId);
    if (cached !== undefined) {
      setAttendanceSnapshots((prev) => (prev[auditId] !== undefined ? prev : { ...prev, [auditId]: cached }));
      return;
    }
    try {
      const url = await fetchAuditImage(auditId);
      setAttendanceSnapshots((prev) => ({ ...prev, [auditId]: url }));
    } catch (error) {
      console.error('Error fetching attendance image:', error);
    }
  }, []);

  const handleRequestActivityAuditImage = useCallback(async (auditId) => {
    if (!auditId) return;
    const cached = peekAuditImage(auditId);
    if (cached !== undefined) {
      setActivityImageUrls((prev) => (prev[auditId] !== undefined ? prev : { ...prev, [auditId]: cached }));
      return;
    }
    try {
      const url = await fetchAuditImage(auditId);
      setActivityImageUrls((prev) => ({ ...prev, [auditId]: url }));
    } catch (error) {
      console.error('Error fetching activity image:', error);
    }
  }, []);


  const handleOpenLogsDatePicker = useCallback((event) => {
    const startDate = logsDateRange[0]?.startDate ? new Date(logsDateRange[0].startDate) : new Date();
    const endDate = logsDateRange[0]?.endDate ? new Date(logsDateRange[0].endDate) : startDate;
    setLogsDateDraftRange([{ startDate, endDate, key: 'selection' }]);
    setLogsStartTimeDraft(logsStartTime);
    setLogsEndTimeDraft(logsEndTime);
    setLogsDatePickerAnchor(event.currentTarget);
  }, [logsDateRange, logsStartTime, logsEndTime]);

  const handleApplyLogsDateFilters = useCallback(() => {
    const appliedDateRange = [{
      startDate: logsDateDraftRange[0]?.startDate || logsDateRange[0]?.startDate || new Date(),
      endDate: logsDateDraftRange[0]?.endDate || logsDateDraftRange[0]?.startDate || logsDateRange[0]?.endDate || new Date(),
      key: 'selection',
    }];
    const appliedStartTime = logsStartTimeDraft;
    const appliedEndTime = logsEndTimeDraft;

    setLogsDateRange(appliedDateRange);
    setLogsStartTime(appliedStartTime);
    setLogsEndTime(appliedEndTime);
    setLogsDatePickerAnchor(null);

    // Reset pagination and fetch logs with new date range
    setLogsPagination(prev => ({ ...prev, offset: 0, current_page: 1 }));
    saveSectionDateRangeToSession(
      personIdFromPath,
      'logs',
      appliedDateRange[0]?.startDate,
      appliedDateRange[0]?.endDate
    );
    updateDateFiltersInUrl(appliedDateRange[0]?.startDate, appliedDateRange[0]?.endDate);
    if (employee?.person_id) {
      fetchLogs(employee.person_id, logsPagination.limit, 0, {
        dateRange: appliedDateRange,
        startTime: appliedStartTime,
        endTime: appliedEndTime,
      });
    }
  }, [logsDateDraftRange, logsStartTimeDraft, logsEndTimeDraft, logsDateRange, logsPagination.limit, fetchLogs, employee?.person_id, personIdFromPath]);

  // Helper function to get time in display timezone
  const getTimeInDisplayTimezone = (dateValue) => {
    let d = null;
    if (dateValue instanceof Date) {
      d = dateValue;
    } else if (typeof dateValue === 'string') {
      const trimmed = dateValue.trim();
      const utcString = trimmed.endsWith('Z') ? trimmed : trimmed + 'Z';
      d = new Date(utcString);
    } else {
      d = new Date(dateValue);
    }

    if (!d || isNaN(d.getTime())) return { hours: 0, minutes: 0 };

    // Get time parts in the browser's timezone (defaults to user's local timezone)
    const formatter = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: false,
    });
    const parts = formatter.formatToParts(d);
    const hours = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
    const minutes = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
    const seconds = parseInt(parts.find(p => p.type === 'second')?.value || '0', 10);
    return { hours, minutes, seconds };
  };

  // Calculate presence for a single day's events
  const calculateDayPresence = (events, isToday) => {
    const startHour = 8; // 8 AM
    const endHour = 24; // 12 AM (midnight)
    const startRange = startHour * 60;
    const endRange = endHour * 60;
    let totalMins = 0;
    let lastCheckInMinute = null;

    const sortedEvents = [...events].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    sortedEvents.forEach((event) => {
      const { hours, minutes } = getTimeInDisplayTimezone(event.created_at);
      const eventMinute = hours * 60 + minutes;
      const clampedEventMinute = Math.max(startRange, Math.min(endRange, eventMinute));

      if (event.action === 'check_in') {
        if (lastCheckInMinute !== null) {
          const clampedStart = Math.max(startRange, Math.min(endRange, lastCheckInMinute));
          if (clampedEventMinute > clampedStart) {
            totalMins += clampedEventMinute - clampedStart;
          }
        }
        lastCheckInMinute = eventMinute;
      } else if (event.action === 'check_out') {
        if (lastCheckInMinute !== null) {
          const clampedStart = Math.max(startRange, Math.min(endRange, lastCheckInMinute));
          if (clampedEventMinute > clampedStart) {
            totalMins += clampedEventMinute - clampedStart;
          }
        }
        lastCheckInMinute = null;
      }
    });

    // If still checked in
    if (lastCheckInMinute !== null) {
      const clampedStart = Math.max(startRange, Math.min(endRange, lastCheckInMinute));
      let endMinute = endRange;
      if (isToday) {
        const nowTz = getTimeInDisplayTimezone(new Date());
        endMinute = Math.max(startRange, Math.min(endRange, nowTz.hours * 60 + nowTz.minutes));
      }
      if (endMinute > clampedStart) {
        totalMins += endMinute - clampedStart;
      }
    }

    return totalMins;
  };

  // Calculate total presence for days on the current attendance page
  const calculateTotalPresence = () => {
    let totalMins = 0;

    attendanceDayGroups.forEach((dayGroup) => {
      const eventsForDate = (dayGroup.events || []).map((event) => ({
        ...event,
        created_at: event.timestamp || event.created_at,
      }));
      if (eventsForDate.length === 0) {
        return;
      }
      const currentDate = dayGroup.date
        ? new Date(`${dayGroup.date}T12:00:00`)
        : new Date(eventsForDate[0].created_at);
      const isDayToday = isSameDay(currentDate, new Date());
      totalMins += calculateDayPresence(eventsForDate, isDayToday);
    });

    const hours = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  // Export functions
  const fetchAttendanceExportMetaData = useCallback(async () => {
    try {
      const metadata = await exportAttendanceLogs({
        meta_only: true,
        is_intrusion: false,
        days: 30,
      });
      const allFields = Array.isArray(metadata?.all_fields)
        ? metadata.all_fields
        : (metadata?.fields || []).map((field, idx) => ({
          field,
          label: metadata?.headers?.[idx] || field,
          enabled: true,
        }));
      if (!allFields.length) {
        console.error('No export fields available');
        return;
      }
      setAttendanceExportFields(allFields);
      const defaultSelection = allFields
        .filter((f) => f.enabled !== false && f.field !== 'presence_time')
        .map((f) => f.field);
      setAttendanceExportSelectedFields(defaultSelection.length ? defaultSelection : allFields
        .filter((f) => f.field !== 'presence_time')
        .map((f) => f.field));
    } catch (error) {
      console.error('Error fetching export metadata:', error);
    }
  }, []);

  const clearAttendanceExportMetaData = useCallback(() => {
    setAttendanceExportFields([]);
    setAttendanceExportSelectedFields([]);
  }, []);

  // Helper function to build export date time range
  const buildExportDateTimeRange = useCallback(() => {
    const startDate = attendanceExportDateRange[0]?.startDate || new Date();
    const endDate = attendanceExportDateRange[0]?.endDate || startDate;
    const parseTime = (timeValue, fallbackHour, fallbackMinute) => {
      if (!timeValue || typeof timeValue !== 'string' || !timeValue.includes(':')) {
        return [fallbackHour, fallbackMinute];
      }
      const [hoursRaw, minutesRaw] = timeValue.split(':');
      const hours = Number(hoursRaw);
      const minutes = Number(minutesRaw);
      if (
        Number.isNaN(hours) ||
        Number.isNaN(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59
      ) {
        return [fallbackHour, fallbackMinute];
      }
      return [hours, minutes];
    };

    const [istStartHour, istStartMinute] = parseTime(attendanceExportStartTime, 0, 0);
    const [istEndHour, istEndMinute] = parseTime(attendanceExportEndTime, 23, 59);

    const startDateIST = new Date(Date.UTC(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      istStartHour,
      istStartMinute,
      0
    ));
    const startDateTimeUTC = new Date(startDateIST.getTime() - (5 * 60 + 30) * 60 * 1000);

    const endDateIST = new Date(Date.UTC(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
      istEndHour,
      istEndMinute,
      0
    ));
    const endDateTimeUTC = new Date(endDateIST.getTime() - (5 * 60 + 30) * 60 * 1000);

    const formattedStart = startDateTimeUTC.toISOString().slice(0, 16);
    const formattedEnd = endDateTimeUTC.toISOString().slice(0, 16);

    return { formattedStart, formattedEnd };
  }, [attendanceExportDateRange, attendanceExportStartTime, attendanceExportEndTime]);

  // Helper to toggle field selection
  const toggleAttendanceExportField = useCallback((field, checked) => {
    setAttendanceExportSelectedFields((prev) => {
      if (checked) {
        return prev.includes(field) ? prev : [...prev, field];
      }
      return prev.filter((f) => f !== field);
    });
  }, []);

  // Helper to move selected field up/down
  const moveSelectedField = useCallback((index, direction) => {
    setAttendanceExportSelectedFields((prev) => {
      const newFields = [...prev];
      if (direction === 'up' && index > 0) {
        [newFields[index], newFields[index - 1]] = [newFields[index - 1], newFields[index]];
      } else if (direction === 'down' && index < newFields.length - 1) {
        [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];
      }
      return newFields;
    });
  }, []);

  const handleOpenAttendanceExportDialog = useCallback(async (formatType = 'csv') => {
    const normalizedFormat = String(formatType || 'csv').toLowerCase() === 'xlsx' ? 'xlsx' : 'csv';
    setAttendanceExportFormat(normalizedFormat);
    await fetchAttendanceExportMetaData();
    setAttendanceExportPersonName(employee?.name || '');
    setAttendanceExportDateRange(aDateRange);
    setAttendanceExportStartTime(aStartTime);
    setAttendanceExportEndTime(aEndTime);
    setAttendanceExportFileName('attendance_logs');
    setAttendanceExportDialogOpen(true);
  }, [fetchAttendanceExportMetaData, employee?.name, aDateRange, aStartTime, aEndTime]);

  const handleExportAttendance = useCallback(async () => {
    if (!attendanceExportSelectedFields.length) {
      alert('Please select at least one field to export');
      return;
    }

    setExportLoading(true);
    try {
      const visibleFields = attendanceExportFields.filter((field) => field.enabled !== false);
      const fieldByName = new Map(visibleFields.map((field) => [field.field, field]));
      const orderedSelectedFields = attendanceExportSelectedFields.filter((field) => fieldByName.has(field));
      let exportSelectedFields = [...orderedSelectedFields];

      if (attendanceExportAllEmployeesPresence) {
        if (!exportSelectedFields.includes('date') && fieldByName.has('date')) {
          exportSelectedFields.unshift('date');
        }
        if (!exportSelectedFields.includes('presence_time') && fieldByName.has('presence_time')) {
          const personNameIndex = exportSelectedFields.indexOf('person_name');
          if (personNameIndex >= 0) {
            exportSelectedFields.splice(personNameIndex + 1, 0, 'presence_time');
          } else {
            const fieldsAfterPresenceTime = ['company_id', 'branch_id', 'check_in_count', 'check_in_timestamps', 'first_check_in', 'check_out_count', 'check_out_timestamps', 'last_check_out'];
            let insertIndex = exportSelectedFields.length;
            for (let i = 0; i < exportSelectedFields.length; i++) {
              if (fieldsAfterPresenceTime.includes(exportSelectedFields[i])) {
                insertIndex = i;
                break;
              }
            }
            exportSelectedFields.splice(insertIndex, 0, 'presence_time');
          }
        }
      }

      if (!exportSelectedFields.length) {
        alert('Select at least one field to export');
        return;
      }

      const { formattedStart, formattedEnd } = buildExportDateTimeRange();
      const jsonData = await exportAttendanceLogs({
        person_id: attendanceExportAllEmployeesPresence ? '' : (employee?.person_id || ''),
        action: attendanceExportAction,
        start_time: formattedStart,
        end_time: formattedEnd,
        premises_filter: attendanceExportPremisesFilter,
        late_filter: attendanceExportLateFilter,
        is_intrusion: false,
        selected_fields: exportSelectedFields,
        include_all_employees_presence: attendanceExportAllEmployeesPresence,
      });

      if (!jsonData?.fields || !jsonData?.headers || jsonData.fields.length !== jsonData.headers.length) {
        console.error('Export attendance: field/header mismatch', jsonData);
        alert('Failed to export attendance logs (invalid export config)');
        return;
      }

      const isEmpty = !jsonData.logs || jsonData.logs.length === 0;
      if (isEmpty) {
        console.warn('Export attendance: no logs returned, exporting headers only', jsonData);
      }

      // Use the sorted field order for CSV export
      const selectedFieldDefs = exportSelectedFields
        .map((field) => fieldByName.get(field))
        .filter(Boolean);
      const exportFields = selectedFieldDefs.map((field) => field.field);
      const exportHeaders = selectedFieldDefs.map((field) => field.label || field.field);

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const safeFileName = (attendanceExportFileName || 'attendance_logs')
        .trim()
        .replace(/[^a-zA-Z0-9_-]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '') || 'attendance_logs';

      if (attendanceExportFormat === 'xlsx') {
        downloadAttendanceXlsxFile(jsonData, exportFields, exportHeaders, safeFileName);
      } else {
        const csvContent = convertJsonToCsv(jsonData, exportFields, exportHeaders);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${safeFileName}_${timestamp}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }

      setAttendanceExportDialogOpen(false);
    } catch (error) {
      console.error('Error exporting attendance:', error);
      alert('Error exporting attendance. Please try again.');
    } finally {
      setExportLoading(false);
    }
  }, [attendanceExportSelectedFields, attendanceExportFileName, employee?.person_id, attendanceExportAction, attendanceExportPremisesFilter, attendanceExportLateFilter, attendanceExportAllEmployeesPresence, attendanceExportFields, buildExportDateTimeRange, attendanceExportFormat]);

  const openAttendanceExportMenu = (event) => {
    setAttendanceExportMenuAnchor(event.currentTarget);
  };

  const closeAttendanceExportMenu = () => {
    setAttendanceExportMenuAnchor(null);
  };

  const handleChooseAttendanceExportFormat = (formatType) => {
    closeAttendanceExportMenu();
    handleOpenAttendanceExportDialog(formatType);
  };

  const fetchAttendanceData = useCallback(async (personId, limit = 10, offset = 0) => {
    if (!personId) return;

    setAttendanceLoading(true);
    setAttendanceError(null);

    try {
      const { formattedStart, formattedEnd } = buildAttendanceDateTimeRange();

      const params = new URLSearchParams({
        person_id: personId,
        start_time: formattedStart,
        end_time: formattedEnd,
        sort_order: 'desc',
        group_by: 'day',
        limit: String(limit),
        offset: String(offset),
      });

      const response = await authenticatedFetch(`/auth/admin/attendance/logs?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch attendance data (${response.status})`);
      }

      const data = await response.json();
      const dayGroups = data.days || [];
      const totalDays = data.pagination?.total ?? dayGroups.length;

      setAttendanceDayGroups(dayGroups);
      setAttendanceTotalEvents(data.total_events ?? 0);
      setAttendancePagination({
        limit,
        offset,
        current_page: limit > 0 ? Math.floor(offset / limit) + 1 : 1,
        total: totalDays,
      });
    } catch (err) {
      console.error('Error fetching attendance data:', err);
      setAttendanceError('Error fetching attendance data');
    } finally {
      setAttendanceLoading(false);
    }
  }, [buildAttendanceDateTimeRange]);

  const fetchGraphAttendanceData = async (personId) => {
    if (!personId) return;

    setAttendanceGraphLoading(true);
    setAttendanceGraphError(null);
    setAttendanceGraphData([]);

    try {
      const { formattedStart, formattedEnd } = buildGraphDateTimeRange();
      const pageLimit = 200;
      const allLogs = [];
      let offset = 0;
      let total = 0;

      for (; ;) {
        const params = new URLSearchParams({
          person_id: personId,
          start_time: formattedStart,
          end_time: formattedEnd,
          sort_order: 'desc',
          group_by: 'day',
          limit: pageLimit.toString(),
          offset: offset.toString(),
        });

        const response = await authenticatedFetch(`/auth/admin/attendance/logs?${params.toString()}`);
        if (!response.ok) {
          throw new Error(`Failed to fetch graph attendance data (${response.status})`);
        }

        const data = await response.json();
        const dayGroups = data.days || [];
        const batch = dayGroups.flatMap((day) => (Array.isArray(day.events) ? day.events : []));
        allLogs.push(...batch);
        total = data.pagination?.total ?? dayGroups.length;

        if (!data.pagination?.has_next || dayGroups.length === 0 || offset + dayGroups.length >= total) {
          break;
        }
        offset += dayGroups.length;
      }

      setAttendanceGraphData(allLogs);
    } catch (err) {
      console.error('Error fetching graph attendance data:', err);
      setAttendanceGraphError('Error fetching attendance graph data');
    } finally {
      setAttendanceGraphLoading(false);
    }
  };

  const handleAttendancePageChange = useCallback((e, page) => {
    const newOffset = (page - 1) * attendancePagination.limit;
    if (employee?.person_id) {
      fetchAttendanceData(employee.person_id, attendancePagination.limit, newOffset);
    }
  }, [attendancePagination.limit, fetchAttendanceData, employee?.person_id]);

  // Helper function to build activity date time range
  const buildActivityDateTimeRange = useCallback(() => {
    const startDate = activityDateRange[0]?.startDate || new Date();
    const endDate = activityDateRange[0]?.endDate || startDate;
    const parseTime = (timeValue, fallbackHour, fallbackMinute) => {
      if (!timeValue || typeof timeValue !== 'string' || !timeValue.includes(':')) {
        return [fallbackHour, fallbackMinute];
      }
      const [hoursRaw, minutesRaw] = timeValue.split(':');
      const hours = Number(hoursRaw);
      const minutes = Number(minutesRaw);
      if (
        Number.isNaN(hours) ||
        Number.isNaN(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59
      ) {
        return [fallbackHour, fallbackMinute];
      }
      return [hours, minutes];
    };

    const [startHour, startMinute] = parseTime(activityStartTime, 0, 0);
    const [endHour, endMinute] = parseTime(activityEndTime, 23, 59);

    // Format as expected by API: "yyyy-MM-dd HH:mm:ss"
    const formattedStart = `${format(startDate, 'yyyy-MM-dd')} ${String(startHour).padStart(2, '0')}:${String(startMinute).padStart(2, '0')}:00`;
    const formattedEnd = `${format(endDate, 'yyyy-MM-dd')} ${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}:59`;

    return { formattedStart, formattedEnd };
  }, [activityDateRange, activityStartTime, activityEndTime]);

  const buildActivityExportDateTimeRange = useCallback(() => {
    const parseTime = (timeValue, fallbackHour, fallbackMinute) => {
      if (!timeValue || typeof timeValue !== 'string' || !timeValue.includes(':')) {
        return [fallbackHour, fallbackMinute];
      }
      const [hoursRaw, minutesRaw] = timeValue.split(':');
      const hours = Number(hoursRaw);
      const minutes = Number(minutesRaw);
      if (
        Number.isNaN(hours) ||
        Number.isNaN(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59
      ) {
        return [fallbackHour, fallbackMinute];
      }
      return [hours, minutes];
    };

    const selection = activityDateRange[0] || { startDate: new Date(), endDate: new Date() };
    const startDate = selection.startDate || new Date();
    const endDate = selection.endDate || startDate;
    const [startHour, startMinute] = parseTime(activityStartTime, 0, 0);
    const [endHour, endMinute] = parseTime(activityEndTime, 23, 59);

    const localStart = new Date(startDate);
    localStart.setHours(startHour, startMinute, 0, 0);
    const localEnd = new Date(endDate);
    localEnd.setHours(endHour, endMinute, 59, 0);
    if (localStart > localEnd) return { error: 'Start date/time cannot be after end date/time' };

    const startDateTimeUTC = new Date(Date.UTC(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      startHour,
      startMinute,
      0
    ));
    const endDateTimeUTC = new Date(Date.UTC(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
      endHour,
      endMinute,
      59
    ));

    return {
      formattedStart: startDateTimeUTC.toISOString().slice(0, 16),
      formattedEnd: endDateTimeUTC.toISOString().slice(0, 16),
    };
  }, [activityDateRange, activityEndTime, activityStartTime]);

  const openActivityExportMenu = (event) => {
    setActivityExportMenuAnchor(event.currentTarget);
  };

  const closeActivityExportMenu = () => {
    setActivityExportMenuAnchor(null);
  };

  const handleExportActivity = useCallback(async (formatType = 'csv') => {
    try {
      closeActivityExportMenu();
      setActivityExportLoading(true);
      const normalizedFormat = String(formatType || 'csv').toLowerCase() === 'xlsx' ? 'xlsx' : 'csv';

      const range = buildActivityExportDateTimeRange();
      if (range?.error) {
        alert(range.error);
        return;
      }

      const safeFileName = (employee?.name ? `activity_logs_${employee.name}` : 'activity_logs')
        .trim()
        .replace(/[^a-zA-Z0-9_-]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '') || 'activity_logs';

      const { blob, filename } = await exportActivityLogs({
        start_time: range.formattedStart,
        end_time: range.formattedEnd,
        person_id: employee?.person_id || undefined,
        person_name: employee?.name || undefined,
        is_unknown: false,
        selected_fields: null,
        export_format: normalizedFormat,
        file_name: safeFileName,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fallbackExt = normalizedFormat === 'xlsx' ? 'xlsx' : 'csv';
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = filename || `${safeFileName}_${timestamp}.${fallbackExt}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export activity logs:', error);
      alert(error?.message || 'Failed to export activity logs');
    } finally {
      setActivityExportLoading(false);
    }
  }, [buildActivityExportDateTimeRange, employee?.name, employee?.person_id]);

  // Apply activity date range
  const applyActivityDateRange = useCallback(() => {
    const current = activityDraftDateRange[0] || { startDate: new Date(), endDate: new Date(), key: 'selection' };
    setActivityDateRange([{ startDate: current.startDate, endDate: current.endDate, key: 'selection' }]);
    setActivityStartTime(activityStartTimeDraft);
    setActivityEndTime(activityEndTimeDraft);
    setActivityDatePickerAnchor(null);
    saveSectionDateRangeToSession(personIdFromPath, 'activity', current.startDate, current.endDate);
  }, [activityDraftDateRange, activityStartTimeDraft, activityEndTimeDraft, personIdFromPath]);

  const fetchActivityData = async (personId) => {
    if (!personId) return;

    setActivityLoading(true);
    setActivityError(null);

    try {
      const { formattedStart, formattedEnd } = buildActivityDateTimeRange();
      const groupKey = `person:${personId}`;

      // Use Promise.allSettled to make both API calls simultaneously
      const [timelineResult, analysisResult] = await Promise.allSettled([
        getActivityGroupTimeline(groupKey, {
          limit: 487,
          offset: 0,
          start_time: formattedStart,
          end_time: formattedEnd,
        }),
        getActivityGroupCameraAnalysis(groupKey, {
          start_time: formattedStart,
          end_time: formattedEnd,
        }),
      ]);

      let timelineData = null;
      let analysisData = null;
      let hasError = false;

      // Process timeline result
      if (timelineResult.status === 'fulfilled') {
        timelineData = timelineResult.value;
      } else {
        console.error('Timeline API failed:', timelineResult.reason);
        hasError = true;
      }

      // Process analysis result
      if (analysisResult.status === 'fulfilled') {
        analysisData = analysisResult.value;
      } else {
        console.error('Analysis API failed:', analysisResult.reason);
        hasError = true;
      }

      if (hasError) {
        setActivityError('Failed to fetch activity data');
        return;
      }

      // Calculate last_seen from the latest event (sort by event_time first)
      const events = timelineData?.events || [];
      const sortedEvents = [...events].sort((a, b) => {
        const timeA = new Date(a.event_time).getTime();
        const timeB = new Date(b.event_time).getTime();
        return timeB - timeA;
      });
      const lastSeen = sortedEvents.length > 0 ? sortedEvents[sortedEvents.length - 1].event_time : null;

      setActivityData({
        timeline: timelineData,
        analysis: analysisData,
        person_id: personId,
        last_seen: lastSeen
      });
    } catch (err) {
      console.error('Error fetching activity data:', err);
      setActivityError('Error fetching activity data');
    } finally {
      setActivityLoading(false);
    }
  };

  const handleLogsPageChange = useCallback((e, page) => {
    const newOffset = (page - 1) * logsPagination.limit;
    if (employee?.person_id) {
      fetchLogs(employee.person_id, logsPagination.limit, newOffset);
    }
  }, [logsPagination.limit, fetchLogs, employee?.person_id]);

  // Fetch timeline attendance data when employee is loaded or timeline filters change
  useEffect(() => {
    if (employee?.person_id && expandedAccordion === 'attendance') {
      fetchAttendanceData(employee.person_id, attendancePagination.limit, 0);
    }
  }, [employee?.person_id, aDateRange, aStartTime, aEndTime, expandedAccordion, fetchAttendanceData]);

  // Fetch graph attendance data independently when graph view is active
  useEffect(() => {
    if (employee?.person_id && expandedAccordion === 'attendance' && attendanceViewMode === 'graph') {
      fetchGraphAttendanceData(employee.person_id);
    }
  }, [employee?.person_id, attendanceGraphDateRange.startDate, attendanceGraphDateRange.endDate, expandedAccordion, attendanceViewMode]);

  // Fetch activity data when employee is loaded or activity date range changes
  useEffect(() => {
    if (employee?.person_id && expandedAccordion === 'activity') {
      fetchActivityData(employee.person_id);
    }
  }, [employee?.person_id, activityDateRange, activityStartTime, activityEndTime, expandedAccordion]);

  // Fetch logs data when employee is loaded or log filters change
  useEffect(() => {
    if (employee?.person_id && expandedAccordion === 'logs-inout') {
      fetchLogs(employee.person_id);
    }
  }, [employee?.person_id, fetchLogs, expandedAccordion, logsDateRange, logsStartTime, logsEndTime, camera]);

  // Reset date filters when switching between employees on the same route
  useEffect(() => {
    if (!personIdFromPath || personIdFromPath === 'employee-details') {
      return;
    }

    if (previousPersonIdRef.current === null) {
      previousPersonIdRef.current = personIdFromPath;
      return;
    }

    if (previousPersonIdRef.current === personIdFromPath) {
      return;
    }

    const previousPersonId = previousPersonIdRef.current;
    previousPersonIdRef.current = personIdFromPath;

    clearEmployeeDetailDateFiltersFromSession(previousPersonId);
    clearEmployeeDetailDateFiltersFromSession(personIdFromPath);

    const attendanceDefault = getDefaultDateRangeForSection('attendance');
    const activityDefault = getDefaultDateRangeForSection('activity');
    const logsDefault = getDefaultDateRangeForSection('logs');
    const activityLogsDefault = getDefaultDateRangeForSection('activity_logs');

    setADateRange(attendanceDefault);
    setADateDraftRange(attendanceDefault);
    setActivityDateRange(activityDefault);
    setActivityDraftDateRange(activityDefault);
    setLogsDateRange(logsDefault);
    setLogsDateDraftRange(logsDefault);
    setActivityLogsDateRange(activityLogsDefault);
    setActivityLogsDraftDateRange(activityLogsDefault);
    clearDateFiltersFromUrl();
  }, [personIdFromPath]);

  // Mark URL filters as hydrated after initial URL-derived state is in place.
  useEffect(() => {
    hasHydratedUrlFiltersRef.current = true;
  }, []);

  // Keep start_date/end_date in URL only on In/Out Logs; clear on other tabs
  useEffect(() => {
    if (!hasHydratedUrlFiltersRef.current) {
      return;
    }

    if (expandedAccordion === 'logs-inout') {
      const startDate = logsDateRange[0]?.startDate;
      const endDate = logsDateRange[0]?.endDate;
      if (startDate && endDate) {
        updateDateFiltersInUrl(startDate, endDate);
      }
      return;
    }

    clearDateFiltersFromUrl();
  }, [expandedAccordion, logsDateRange]);

  // Initialize logs date range and camera from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const startDateParam = urlParams.get('start_date');
    const endDateParam = urlParams.get('end_date');
    const cameraParam = urlParams.get('logs_camera');

    if (startDateParam || endDateParam) {
      const startDate = startDateParam ? new Date(`${startDateParam}T00:00:00`) : new Date();
      const endDate = endDateParam ? new Date(`${endDateParam}T00:00:00`) : startDate;

      // Validate dates
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        setLogsDateRange([{
          startDate,
          endDate,
          key: 'selection'
        }]);
        setLogsDateDraftRange([{
          startDate,
          endDate,
          key: 'selection'
        }]);
      }
    }

    // Initialize camera from URL parameter
    if (cameraParam) {
      setCamera(cameraParam);
    }
  }, []);

  // Update URL parameters when camera filter changes
  useEffect(() => {
    if (!hasHydratedUrlFiltersRef.current) {
      return;
    }

    const url = new URL(window.location.href);

    if (camera) {
      url.searchParams.set('logs_camera', camera);
    } else {
      url.searchParams.delete('logs_camera');
    }

    window.history.replaceState({}, '', url);
  }, [camera]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(detailBackPath)}
          sx={{ mt: 2 }}
        >
          {detailBackLabel}
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 0 }, pl: { xs: 1.5, sm: 2, md: 0 }, maxWidth: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1.25 }}>
          <Typography variant="h4" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
            {detailTitle}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
              {employee?.name || 'N/A'}
            </Typography>
            <Avatar
              sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}
              src={faceImages[employee?.faces?.find(face => !face.deleted_at && face.face_angle === 'front')?.face_id] || faceImages[employee?.faces?.find(face => !face.deleted_at)?.face_id]}
            >
              {employee?.name?.charAt(0)?.toUpperCase()}
            </Avatar>
          </Box>
        </Box>

        {/* Quick Navigation */}
        <Box sx={{ display: 'block', position: 'sticky', top: 8, zIndex: 10, mb: 2 }}>
          <Paper variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider', bgcolor: 'background.default' }}>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.75, ml: 1, mt: 1, display: 'block' }}>
                Quick Navigation
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  overflowX: 'auto',
                  mr: 1,
                  ml: 1,
                  pb: 0.25,
                  pr: 0.25,
                  scrollbarWidth: 'none',         // Firefox
                  msOverflowStyle: 'none',        // IE and Edge
                  '&::-webkit-scrollbar': {
                    display: 'none',              // Chrome, Safari, Opera
                  },
                }}
              >
                {quickNavigationItems.map((item) => {
                  const ItemIcon = item.icon;
                  const isActive = expandedAccordion === item.id;
                  return (
                    <Button
                      key={item.id}
                      variant={isActive ? 'contained' : 'outlined'}
                      color={isActive ? 'primary' : 'inherit'}
                      onClick={() => handleAccordionNavigation(item.id)}
                      startIcon={<ItemIcon sx={{ fontSize: 14 }} />}
                      size="small"
                      sx={{
                        flexShrink: 0,
                        minWidth: 'fit-content',
                        whiteSpace: 'nowrap',
                        textTransform: 'capitalize',
                        borderRadius: 4,
                        mb: 1,
                        px: 1,
                        py: 0.35,
                        fontSize: '0.72rem',
                        fontWeight: 500,
                        lineHeight: 1.15,
                      }}
                    >
                      {formatQuickNavLabel(item.label)}
                    </Button>
                  );
                })}
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Main Content - Sections */}
        <Box
          sx={{
            flex: 3,
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            mt: 0,
            height: { md: '100%' },
            // scrollbarGutter: { md: 'stable' },
            // '&::-webkit-scrollbar': {
            //   width: 8,
            // },
            // '&::-webkit-scrollbar-track': {
            //   backgroundColor: 'rgba(0,0,0,0.04)',
            //   borderRadius: 8,
            // },
            // '&::-webkit-scrollbar-thumb': {
            //   backgroundColor: 'rgba(0,0,0,0.28)',
            //   borderRadius: 8,
            // },
            // '&::-webkit-scrollbar-thumb:hover': {
            //   backgroundColor: 'rgba(0,0,0,0.38)',
            // },
          }}
        >
          {/* Basic Details Section */}
          <Box ref={basicDetailsRef}>
            <Collapse in={expandedAccordion === 'basic-details'} timeout={220} mountOnEnter unmountOnExit>
              <Box>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'medium', mb: 1 }}>
                  Basic Details
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 3,
                    alignItems: 'flex-start',
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: '100%', md: 'auto' },
                      flex: { xs: '1 1 100%', md: '0 0 400px' },
                      flexShrink: 0,
                    }}
                  >
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        borderColor: 'divider',
                        bgcolor: 'background.default',
                        height: '100%',
                      }}
                    >
                      <Stack spacing={1.5} alignItems="center">
                        <Box
                          onClick={() => {
                            if (displayFace) {
                              handleViewFaceImage(displayFace);
                            }
                          }}
                          sx={{
                            width: { xs: 180, sm: 200 },
                            height: { xs: 180, sm: 200 },
                            border: 1,
                            borderColor: 'divider',
                            overflow: 'hidden',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'grey.100',
                            cursor: displayFace ? 'pointer' : 'default',
                          }}
                        >
                          {displayFaceImage ? (
                            <img
                              src={displayFaceImage}
                              alt={`Face - ${displayFace?.face_angle || 'unknown'}`}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <Avatar sx={{ width: 72, height: 72, bgcolor: 'primary.main', fontSize: 32 }}>
                              {employee?.name?.charAt(0)?.toUpperCase() || '?'}
                            </Avatar>
                          )}
                        </Box>

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="center">
                          <Chip size="small" color="primary" variant="outlined" label={displayFace?.face_angle || 'No face selected'} />
                          <Chip size="small" label={`Faces: ${activeFaces.length}`} />
                        </Stack>

                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                          {activeFaces.length > 0 ? 'Tap face tile to switch preview' : 'No active face images available'}
                        </Typography>
                      </Stack>

                      {activeFaces.length > 0 && (
                        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                          {activeFaces.map((face) => {
                            const isActiveFace = displayFace?.face_id === face.face_id;
                            return (
                              <Box
                                key={face.face_id}
                                onClick={() => setSelectedFace(face)}
                                sx={{
                                  width: 74,
                                  p: 0.5,
                                  borderRadius: 1.5,
                                  border: 1,
                                  borderColor: isActiveFace ? 'primary.main' : 'divider',
                                  bgcolor: isActiveFace ? 'primary.50' : 'background.paper',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    borderColor: 'primary.main',
                                    bgcolor: 'primary.50',
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    width: '100%',
                                    aspectRatio: '1 / 1',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'grey.100',
                                  }}
                                >
                                  {faceImages[face.face_id] ? (
                                    <img
                                      src={faceImages[face.face_id]}
                                      alt={`Face - ${face.face_angle || 'unknown'}`}
                                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                  ) : (
                                    <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.main', fontSize: 16 }}>
                                      {employee?.name?.charAt(0)?.toUpperCase() || '?'}
                                    </Avatar>
                                  )}
                                </Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    mt: 0.5,
                                    textAlign: 'center',
                                    fontWeight: isActiveFace ? 600 : 400,
                                    color: isActiveFace ? 'primary.main' : 'text.secondary',
                                    textTransform: 'capitalize',
                                  }}
                                >
                                  {(face.face_angle && face.face_angle.split(/[_-]/)[0]) || 'Unknown'}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Paper>
                  </Box>

                  <Box sx={{ minWidth: 0, flex: 1, width: { xs: '100%', md: 'auto' } }}>
                    <TodayAttendanceMetricsCards
                      personId={employee?.person_id}
                      enabled={expandedAccordion === 'basic-details'}
                    />

                    <Box
                      sx={{
                        mt: 2,
                        width: '100%',
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                        gap: 2,
                        alignContent: 'start',
                        '& .MuiFormControl-root': {
                          width: '100%',
                        },
                      }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        label="Company Name"
                        value={employee?.company_name || 'N/A'}
                        InputProps={{ readOnly: true }}
                        sx={{
                          '& .MuiInputBase-input': {
                            fontWeight: 500,
                          },
                        }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Employee Code"
                        value={employee?.employee_code || 'N/A'}
                        InputProps={{ readOnly: true }}
                        sx={{
                          '& .MuiInputBase-input': {
                            fontWeight: 500,
                          },
                        }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Designation"
                        value={employee?.designation || 'N/A'}
                        InputProps={{ readOnly: true }}
                        sx={{
                          '& .MuiInputBase-input': {
                            fontWeight: 500,
                          },
                        }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Department"
                        value={employee?.department || 'N/A'}
                        InputProps={{ readOnly: true }}
                        sx={{
                          '& .MuiInputBase-input': {
                            fontWeight: 500,
                          },
                        }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Branch Address"
                        value={employee?.branch_address || 'N/A'}
                        InputProps={{ readOnly: true }}
                        multiline
                        minRows={2}
                        sx={{
                          gridColumn: { xs: 'auto', sm: '1 / -1' },
                          '& .MuiInputBase-input': {
                            fontWeight: 500,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Collapse>
          </Box>

          {/* Attendance Section */}
          <Box ref={attendanceRef}>
            <Collapse in={expandedAccordion === 'attendance'} timeout={220} mountOnEnter unmountOnExit>
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                    Attendance
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Timeline view">
                      <IconButton
                        size="small"
                        onClick={() => setAttendanceViewMode('timeline')}
                        disabled={attendanceDayGroups.length === 0}
                        aria-label="Timeline view"
                        sx={{
                          width: 40,
                          height: 32,
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: attendanceViewMode === 'timeline' ? 'primary.50' : 'background.paper',
                          color: attendanceViewMode === 'timeline' ? 'primary.main' : 'text.secondary',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'primary.50',
                            color: 'primary.main',
                          },
                        }}
                      >
                        <ViewTimeline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Graph view">
                      <IconButton
                        size="small"
                        onClick={() => setAttendanceViewMode('graph')}
                        aria-label="Graph view"
                        sx={{
                          width: 40,
                          height: 32,
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: attendanceViewMode === 'graph' ? 'primary.50' : 'background.paper',
                          color: attendanceViewMode === 'graph' ? 'primary.main' : 'text.secondary',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'primary.50',
                            color: 'primary.main',
                          },
                        }}
                      >
                        <ShowChart fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
                {/* Date Filter Section — timeline only */}
                {attendanceViewMode === 'timeline' && (
                  <Box sx={{ mb: 3 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box
                        onClick={handleOpenAttendanceDatePicker}
                        sx={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 1.5,
                          py: 0,
                          height: 40,
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: 'grey.50',
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: 'grey.100',
                            borderColor: 'primary.main',
                          },
                        }}
                      >
                        <CalendarMonth fontSize="small" color="action" />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {isSameDay(aDateRange[0].startDate, aDateRange[0].endDate)
                            ? `${format(aDateRange[0].startDate, 'MMM dd, yyyy')}${aStartTime !== '00:00' || aEndTime !== '23:59' ? ` • ${aStartTime} - ${aEndTime}` : ''}`
                            : `${format(aDateRange[0].startDate, 'MMM dd, yyyy')} - ${format(aDateRange[0].endDate, 'MMM dd, yyyy')}${aStartTime !== '00:00' || aEndTime !== '23:59' ? ` • ${aStartTime} - ${aEndTime}` : ''}`
                          }
                        </Typography>
                      </Box>
                      <Box sx={{ flexShrink: 0 }}>
                        <Button
                          variant="outlined"
                          startIcon={<FileDownload />}
                          endIcon={<ArrowDropDown />}
                          onClick={openAttendanceExportMenu}
                          disabled={attendanceDayGroups.length === 0}
                          sx={{
                            minWidth: 'auto',
                            px: 2,
                            py: 0,
                            height: 40,
                            borderColor: 'divider',
                            '&:hover': {
                              borderColor: 'primary.main',
                              bgcolor: 'primary.50',
                            },
                          }}
                        >
                          Export
                        </Button>
                        <Menu
                          anchorEl={attendanceExportMenuAnchor}
                          open={attendanceExportMenuOpen}
                          onClose={closeAttendanceExportMenu}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                          <MenuItem onClick={() => handleChooseAttendanceExportFormat('csv')}>Export in CSV</MenuItem>
                          <MenuItem onClick={() => handleChooseAttendanceExportFormat('xlsx')}>Export in XLSX</MenuItem>
                        </Menu>
                      </Box>
                    </Stack>
                    <Popover
                      open={aDatePickerOpen}
                      anchorEl={aDatePickerAnchor}
                      onClose={() => setADatePickerAnchor(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                      slotProps={{
                        paper: {
                          sx: {
                            mt: 1,
                            p: 2,
                            borderRadius: 2,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                            maxWidth: { xs: '95vw', sm: 'auto' }
                          }
                        }
                      }}
                    >
                      <Box>
                        {/* Date Range Picker - Responsive */}
                        <Box sx={{
                          '& .rdrCalendarWrapper': {
                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                          },
                          '& .rdrMonth': {
                            width: { xs: '280px', sm: '300px' },
                          },
                        }}>
                          <DateRangePicker
                            ranges={aDateDraftRange}
                            onChange={(ranges) => setADateDraftRange([ranges.selection])}
                            moveRangeOnFirstSelection={false}
                            rangeColors={['#3f51b5']}
                            months={1}
                            direction="horizontal"
                            showDateDisplay={false}
                            minDate={new Date(2020, 0, 1)}
                            maxDate={new Date(2030, 11, 31)}
                          />
                        </Box>

                        {/* Time Inputs */}
                        <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                          <TextField
                            label="Start Time"
                            type="time"
                            value={aStartTimeDraft}
                            onChange={(e) => setAStartTimeDraft(e.target.value)}
                            size="small"
                            sx={{ minWidth: 120 }}
                            inputProps={{
                              step: 300, // 5 min steps
                            }}
                          />
                          <TextField
                            label="End Time"
                            type="time"
                            value={aEndTimeDraft}
                            onChange={(e) => setAEndTimeDraft(e.target.value)}
                            size="small"
                            sx={{ minWidth: 120 }}
                            inputProps={{
                              step: 300, // 5 min steps
                            }}
                          />
                        </Box>

                        {/* Action Buttons */}
                        <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button onClick={handleResetAttendanceDateFilters} variant="text" size="small">
                            Reset
                          </Button>
                          <Button onClick={() => setADatePickerAnchor(null)} variant="outlined" size="small">
                            Cancel
                          </Button>
                          <Button
                            onClick={handleApplyAttendanceDateFilters}
                            variant="contained"
                            size="small"
                            sx={{ ml: 1 }}
                          >
                            Apply
                          </Button>
                        </Box>
                      </Box>
                    </Popover>
                  </Box>
                )}

                {attendanceViewMode === 'graph' ? (
                  attendanceGraphLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 460, py: 6 }}>
                      <CircularProgress />
                    </Box>
                  ) : attendanceGraphError ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {attendanceGraphError}
                    </Alert>
                  ) : (
                    <Box
                      sx={{
                        bgcolor: '#fff',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        p: { xs: 1.5, sm: 2.5 },
                        maxWidth: '100%',
                        overflowX: 'hidden',
                      }}
                    >
                      <AttendancePresenceGraph
                        events={attendanceGraphData.map((log) => ({
                          ...log,
                          created_at: log.timestamp || log.created_at,
                        }))}
                        dateRangeStart={attendanceGraphDateRange.startDate}
                        dateRangeEnd={attendanceGraphDateRange.endDate}
                        periodDays={attendanceGraphPeriodDays}
                        onPeriodChange={handleGraphPeriodChange}
                        onCustomDateRangeApply={handleGraphCustomDateRangeApply}
                      />
                    </Box>
                  )
                ) : attendanceLoading ? (
                  <AttendanceTimelineSkeleton />
                ) : attendanceError ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {attendanceError}
                  </Alert>
                ) : attendanceDayGroups.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No attendance records found for this date
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ bgcolor: '#fafbfc', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    {/* Header Section */}
                    <Box sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: '#fff', borderBottom: '1px solid', borderRadius: 1, borderColor: 'divider' }}>
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={`In: ${attendancePageEvents.filter(log => log.action === 'check_in').length}`} sx={{ height: { xs: 20, sm: 24 }, fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600, bgcolor: '#dcfce7', color: '#166534' }} />
                        <Chip size="small" label={`Out: ${attendancePageEvents.filter(log => log.action === 'check_out').length}`} sx={{ height: { xs: 20, sm: 24 }, fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600, bgcolor: '#fef9c3', color: '#854d0e' }} />
                        <Chip size="small" label={`Presence: ${calculateTotalPresence()}`} sx={{ height: { xs: 20, sm: 24 }, fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600, bgcolor: '#dbeafe', color: '#1d4ed8' }} />
                        {attendanceTotalEvents > attendancePageEvents.length && (
                          <Chip size="small" label={`${attendanceTotalEvents} events in range`} sx={{ height: { xs: 20, sm: 24 }, fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600, bgcolor: '#f3f4f6', color: '#6b7280' }} />
                        )}
                      </Stack>
                    </Box>

                    {/* Timeline Content */}
                    <Box sx={{ p: { xs: 2, sm: 3 }, borderRadius: 1, bgcolor: '#fafbfc' }}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        justifyContent="space-between"
                        spacing={{ xs: 1, sm: 0 }}
                        sx={{ mb: 2.5 }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                            Daily Timeline
                          </Typography>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#40518A' }} />
                        </Stack>
                        <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} flexWrap="wrap" useFlexGap>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Box sx={{ width: { xs: 6, sm: 8 }, height: { xs: 6, sm: 8 }, bgcolor: '#86efac', borderRadius: '50%' }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600 }}>Present</Typography>
                          </Stack>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Box sx={{ width: { xs: 6, sm: 8 }, height: { xs: 6, sm: 8 }, bgcolor: '#fecaca', borderRadius: '50%' }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600 }}>Away</Typography>
                          </Stack>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Box sx={{ width: { xs: 6, sm: 8 }, height: { xs: 6, sm: 8 }, bgcolor: '#22c55e', borderRadius: '50%' }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600 }}>In</Typography>
                          </Stack>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Box sx={{ width: { xs: 6, sm: 8 }, height: { xs: 6, sm: 8 }, bgcolor: '#f59e0b', borderRadius: '50%' }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600 }}>Out</Typography>
                          </Stack>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Box sx={{ width: { xs: 6, sm: 8 }, height: { xs: 6, sm: 8 }, bgcolor: '#64748b', borderRadius: '50%' }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600 }}>Missing</Typography>
                          </Stack>
                        </Stack>
                      </Stack>

                      <Box
                        sx={{
                          bgcolor: '#fff',
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          p: { xs: 1.5, sm: 2.5 },
                          maxWidth: '100%',
                          overflowX: 'hidden',
                        }}
                      >
                        {attendanceDayGroups.map((dayGroup) => {
                          const currentDate = dayGroup.date
                            ? new Date(`${dayGroup.date}T12:00:00`)
                            : new Date();
                          const eventsForDate = (dayGroup.events || []).map((log) => ({
                            ...log,
                            created_at: log.timestamp || log.created_at,
                            image_available: true,
                          }));

                          if (eventsForDate.length === 0) {
                            return null;
                          }

                          return (
                            <Box key={dayGroup.date || currentDate.toISOString()} sx={{ mb: 3 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700, color: '#1f2937', mb: 1.25, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
                              >
                                {format(currentDate, 'EEE, MMM dd, yyyy')}
                              </Typography>
                              <DayTimelineBar
                                date=""
                                events={eventsForDate}
                                isToday={isSameDay(currentDate, new Date())}
                                hideSummary={false}
                                onRequestMarkerImage={handleRequestMarkerImage}
                                snapshotCache={attendanceSnapshots}
                              />
                            </Box>
                          );
                        })}
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 2 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            Showing {attendancePagination.total === 0 ? 0 : attendancePagination.offset + 1}-{Math.min(attendancePagination.offset + attendancePagination.limit, attendancePagination.total)} of {attendancePagination.total} results
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" color="text.secondary">Tiles per page:</Typography>
                            <Select
                              size="small"
                              value={attendancePagination.limit}
                              onChange={(e) => {
                                const newLimit = parseInt(e.target.value, 10);
                                setAttendancePagination((prev) => ({ ...prev, limit: newLimit, offset: 0, current_page: 1 }));
                                if (employee?.person_id) {
                                  fetchAttendanceData(employee.person_id, newLimit, 0);
                                }
                              }}
                              sx={{ minWidth: 90 }}
                            >
                              <MenuItem value={10}>10</MenuItem>
                              <MenuItem value={25}>25</MenuItem>
                              <MenuItem value={50}>50</MenuItem>
                              <MenuItem value={100}>100</MenuItem>
                            </Select>
                          </Stack>
                        </Stack>
                        <Pagination
                          count={Math.max(1, Math.ceil((attendancePagination.total || 0) / attendancePagination.limit))}
                          page={attendancePagination.current_page}
                          onChange={handleAttendancePageChange}
                          showFirstButton
                          showLastButton
                        />
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>

          {/* Activity Section */}
          <Box ref={activityRef}>
            <Collapse in={expandedAccordion === 'activity'} timeout={220} mountOnEnter unmountOnExit>
              <Box>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'medium', mb: 1 }}>
                  Activity
                </Typography>
                {/* Activity Date Filter Section */}
                <Box sx={{ mb: 3 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      onClick={handleOpenActivityDatePicker}
                      sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 0,
                        height: 40,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        bgcolor: 'grey.50',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'grey.100',
                          borderColor: 'primary.main',
                        },
                      }}
                    >
                      <CalendarMonth fontSize="small" color="action" />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {isSameDay(activityDateRange[0].startDate, activityDateRange[0].endDate)
                          ? `${format(activityDateRange[0].startDate, 'MMM dd, yyyy')}${activityStartTime !== '00:00' || activityEndTime !== '23:59' ? ` • ${activityStartTime} - ${activityEndTime}` : ''}`
                          : `${format(activityDateRange[0].startDate, 'MMM dd')} - ${format(activityDateRange[0].endDate, 'MMM dd, yyyy')}${activityStartTime !== '00:00' || activityEndTime !== '23:59' ? ` • ${activityStartTime} - ${activityEndTime}` : ''}`}
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      startIcon={<FileDownload />}
                      endIcon={<ArrowDropDown />}
                      onClick={openActivityExportMenu}
                      disabled={activityLoading || activityExportLoading}
                      sx={{
                        minWidth: 'auto',
                        px: 2,
                        py: 0,
                        height: 40,
                        borderColor: 'divider',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'primary.50',
                        },
                      }}
                    >
                      Export
                    </Button>
                    <Menu
                      anchorEl={activityExportMenuAnchor}
                      open={activityExportMenuOpen}
                      onClose={closeActivityExportMenu}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      <MenuItem onClick={() => handleExportActivity('csv')}>Export in CSV</MenuItem>
                      <MenuItem onClick={() => handleExportActivity('xlsx')}>Export in XLSX</MenuItem>
                    </Menu>
                  </Stack>
                  <Popover
                    open={activityDatePickerOpen}
                    anchorEl={activityDatePickerAnchor}
                    onClose={() => setActivityDatePickerAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{
                      paper: {
                        sx: {
                          mt: 1,
                          p: 2,
                          borderRadius: 2,
                          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                          maxWidth: { xs: '95vw', sm: 'auto' }
                        }
                      }
                    }}
                  >
                    <Box>
                      <Box sx={{
                        '& .rdrCalendarWrapper': {
                          fontSize: { xs: '0.8rem', sm: '0.875rem' }
                        },
                        '& .rdrMonth': {
                          width: { xs: '280px', sm: '300px' },
                        },
                      }}>
                        <DateRangePicker
                          ranges={activityDraftDateRange}
                          onChange={(ranges) => setActivityDraftDateRange([ranges.selection])}
                          moveRangeOnFirstSelection={false}
                          months={1}
                          direction="horizontal"
                          rangeColors={['#40518A']}
                          maxDate={new Date()}
                          showDateDisplay={false}
                        />
                      </Box>

                      <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                        <TextField
                          label="Start Time"
                          type="time"
                          size="small"
                          value={activityStartTimeDraft}
                          onChange={(e) => setActivityStartTimeDraft(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 300 }}
                          sx={{ minWidth: { xs: '100%', sm: 120 } }}
                        />
                        <TextField
                          label="End Time"
                          type="time"
                          size="small"
                          value={activityEndTimeDraft}
                          onChange={(e) => setActivityEndTimeDraft(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 300 }}
                          sx={{ minWidth: { xs: '100%', sm: 120 } }}
                        />
                      </Box>

                      <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <Button onClick={handleResetActivityDateFilters} variant="text" size="small">
                          Reset
                        </Button>
                        <Button onClick={() => setActivityDatePickerAnchor(null)} variant="outlined" size="small">
                          Cancel
                        </Button>
                        <Button
                          onClick={applyActivityDateRange}
                          variant="contained"
                          size="small"
                          sx={{ ml: 1 }}
                        >
                          Apply
                        </Button>
                      </Box>
                    </Box>
                  </Popover>
                </Box>

                {activityLoading ? (
                  <ActivityTimelineSkeleton />
                ) : activityError ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {activityError}
                  </Alert>
                ) : !activityData.timeline || activityData.timeline.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No activity records found for this date
                    </Typography>
                  </Box>
                ) : (
                  <ActivityTimelineInline
                    group={{
                      group_id: `person:${activityData.person_id}`,
                      person_name: employee?.name || 'Unknown',
                      person_id: activityData.person_id,
                      employee_code: employee?.employee_code || 'N/A',
                      last_seen: activityData.last_seen
                    }}
                    events={activityData.timeline.events || []}
                    loading={false}
                    snapshotCache={activityImageUrls}
                    onRequestAuditImage={handleRequestActivityAuditImage}
                    thumbnail={faceImages[activityData.person_id]}
                    analysis={activityData.analysis}
                  />
                )}
              </Box>
            </Collapse>
          </Box>

          {/* Logs Section */}
          <Box ref={logsRef}>
            <Collapse in={expandedAccordion === 'logs-inout'} timeout={220} mountOnEnter unmountOnExit>
              <Box>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'medium', mb: 3 }}>
                  In/Out Logs
                </Typography>

                {/* Logs Filters */}
                <Box sx={pageFiltersToolbarSx}>
                  <Stack {...pageFiltersStackSx}>
                    <Box sx={pageFiltersControlsSx}>
                      <FormControl
                        size="small"
                        sx={pageFilterFormControlSx(220)}
                      >
                        <InputLabel id="camera-label" shrink>
                          Select Camera Type
                        </InputLabel>
                        <Select
                          labelId="camera-label"
                          value={camera}
                          label="Select Camera Type"
                          onChange={(e) => setCamera(e.target.value)}
                          displayEmpty
                          renderValue={(v) => v ? (v === 'in' ? 'Camera: In' : 'Camera: Out') : 'All'}
                        >
                          <MenuItem value="">All</MenuItem>
                          <MenuItem value="in">Camera: In</MenuItem>
                          <MenuItem value="out">Camera: Out</MenuItem>
                        </Select>
                      </FormControl>

                      <Box
                        onClick={handleOpenLogsDatePicker}
                        sx={pageFilterDatePickerSx(220)}
                      >
                        <CalendarMonth fontSize="small" color="action" />
                        <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                          {isSameDay(logsDateRange[0].startDate, logsDateRange[0].endDate)
                            ? `${format(logsDateRange[0].startDate, 'MMM dd, yyyy')}${logsStartTime !== '00:00' || logsEndTime !== '23:59' ? ` • ${logsStartTime} - ${logsEndTime}` : ''}`
                            : `${format(logsDateRange[0].startDate, 'MMM dd, yyyy')} - ${format(logsDateRange[0].endDate, 'MMM dd, yyyy')}${logsStartTime !== '00:00' || logsEndTime !== '23:59' ? ` • ${logsStartTime} - ${logsEndTime}` : ''}`}
                        </Typography>
                      </Box>

                      <ClearAllFiltersButton
                        onClear={handleClearInOutLogsFilters}
                        disabled={!hasActiveInOutLogsFilters}
                      />
                    </Box>

                    <Box sx={pageFiltersActionsSx}>
                      <Button
                        variant="outlined"
                        startIcon={<FileDownload />}
                        endIcon={<ArrowDropDown />}
                        onClick={openLogsExportMenu}
                        disabled={logsExportLoading || logsLoading}
                        sx={{
                          ...pageFilterActionButtonSx,
                          borderColor: 'divider',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'primary.50',
                          },
                        }}
                      >
                        {logsExportLoading ? 'Exporting...' : 'Export'}
                      </Button>
                    </Box>
                  </Stack>

                  <Menu
                    anchorEl={logsExportMenuAnchor}
                    open={logsExportMenuOpen}
                    onClose={closeLogsExportMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <MenuItem onClick={() => handleChooseLogsExportFormat('csv')}>Export in CSV</MenuItem>
                    <MenuItem onClick={() => handleChooseLogsExportFormat('xlsx')}>Export in XLSX</MenuItem>
                  </Menu>

                  <Popover
                    open={logsDatePickerOpen}
                    anchorEl={logsDatePickerAnchor}
                    onClose={() => setLogsDatePickerAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{
                      paper: {
                        sx: {
                          mt: 1,
                          p: 2,
                          borderRadius: 2,
                          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                          maxWidth: { xs: '95vw', sm: 'auto' }
                        }
                      }
                    }}
                  >
                    <Box>
                      <Box sx={{
                        '& .rdrCalendarWrapper': {
                          fontSize: { xs: '0.8rem', sm: '0.875rem' }
                        },
                        '& .rdrMonth': {
                          width: { xs: '280px', sm: '300px' },
                        },
                      }}>
                        <DateRangePicker
                          ranges={logsDateDraftRange}
                          onChange={(ranges) => setLogsDateDraftRange([ranges.selection])}
                          moveRangeOnFirstSelection={false}
                          rangeColors={['#40518A']}
                          months={1}
                          direction="horizontal"
                          showDateDisplay={false}
                          maxDate={new Date()}
                        />
                      </Box>

                      <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                        <TextField
                          label="Start Time"
                          type="time"
                          size="small"
                          value={logsStartTimeDraft}
                          onChange={(e) => setLogsStartTimeDraft(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 300 }}
                          sx={{ minWidth: { xs: '100%', sm: 120 } }}
                        />
                        <TextField
                          label="End Time"
                          type="time"
                          size="small"
                          value={logsEndTimeDraft}
                          onChange={(e) => setLogsEndTimeDraft(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 300 }}
                          sx={{ minWidth: { xs: '100%', sm: 120 } }}
                        />
                      </Box>

                      <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <Button
                          onClick={() => {
                            const today = new Date();
                            setLogsDateDraftRange([{ startDate: today, endDate: today, key: 'selection' }]);
                            setLogsStartTimeDraft('00:00');
                            setLogsEndTimeDraft('23:59');
                          }}
                          variant="text"
                          size="small"
                        >
                          Reset
                        </Button>
                        <Button onClick={() => setLogsDatePickerAnchor(null)} variant="outlined" size="small">
                          Cancel
                        </Button>
                        <Button
                          onClick={handleApplyLogsDateFilters}
                          variant="contained"
                          size="small"
                          sx={{ ml: 1 }}
                        >
                          Apply
                        </Button>
                      </Box>
                    </Box>
                  </Popover>
                </Box>
                {logsLoading ? (
                  <Box>
                    {/* Date header skeleton */}
                    <Box sx={{ width: 180, height: 28, borderRadius: 1, mb: 2, ...shimmerStyles }} />
                    {/* Tiles skeleton - responsive grid */}
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'repeat(2, 1fr)',
                        sm: 'repeat(3, 1fr)',
                        md: 'repeat(4, 1fr)',
                        lg: 'repeat(5, 1fr)',
                        xl: 'repeat(5, 1fr)',
                      },
                      gap: 1.5,
                    }}>
                      {Array.from({ length: 12 }).map((_, idx) => (
                        <LogsTileSkeleton key={`logs-skel-${idx}`} />
                      ))}
                    </Box>
                  </Box>
                ) : logsError ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {logsError}
                  </Alert>
                ) : logsData.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No logs found for this employee
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    {Object.entries(groupLogsByDate(logsData)).map(([date, items]) => {
                      // Backend already returns bucketized top logs; build bucket structures for UI rendering
                      const trackBuckets = items.map((log) => ({
                        bucketKey: log.bucket_key || `track_${log.audit_id}`,
                        representative: log,
                        logs: (Array.isArray(log.bucket_logs) && log.bucket_logs.length > 0) ? log.bucket_logs : [log],
                        stack_size: log.stack_size || (Array.isArray(log.bucket_logs) ? log.bucket_logs.length : 1),
                      }));

                      return (
                        <Box key={date} sx={{ mb: 4 }}>
                          {/* Date header */}
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 600, mb: 2, color: '#333' }}
                          >
                            {date}
                          </Typography>

                          {/* Logs for this date */}
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: {
                                xs: 'repeat(2, 1fr)',
                                sm: 'repeat(3, 1fr)',
                                md: 'repeat(4, 1fr)',
                                lg: 'repeat(5, 1fr)',
                                xl: 'repeat(5, 1fr)',
                              },
                              gap: 1.5,
                            }}
                          >
                            {trackBuckets.map((bucket) => {
                              const log = bucket.representative;
                              const bucketLogs = bucket.logs;
                              const stackImages = buildPrioritizedStackImages(bucketLogs, log, {});
                              const capturedTs = getCapturedTimestamp(log);
                              const requestSentTs = getRequestSentTimestamp(log);
                              const apiSubmitTs = getApiSubmitTimestamp(log);
                              const apiReceivedTs = getApiReceivedTimestamp(log);

                              return (
                                <Box
                                  key={log.audit_id}
                                  sx={{
                                    display: 'flex',
                                    '& .MuiCard-root': {
                                      height: '100%',
                                      width: '100%',
                                    },
                                  }}
                                >
                                  <AuditTile
                                    log={log}
                                    lazyLoad
                                    stackImages={stackImages}
                                    bucketLogs={bucketLogs}
                                    disableEmployeeLink
                                    capturedTs={capturedTs}
                                    apiReceivedTs={apiReceivedTs}
                                    onClick={() => openTimingDialog({
                                      log: {
                                        ...log,
                                        capturedTs,
                                        requestSentTs,
                                        apiSubmitTs,
                                        apiReceivedTs,
                                      },
                                      stack: stackImages,
                                    })}
                                    timeOnly={false}
                                  />
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* Pagination */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 2 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Showing {logsPagination.total === 0 ? 0 : logsPagination.offset + 1}-{Math.min(logsPagination.offset + logsPagination.limit, logsPagination.total)} of {logsPagination.total} results
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" color="text.secondary">Tiles per page:</Typography>
                      <Select
                        size="small"
                        value={logsPagination.limit}
                        onChange={(e) => {
                          setLogsPagination(prev => ({ ...prev, limit: parseInt(e.target.value), offset: 0, current_page: 1 }));
                          if (employee?.person_id) {
                            fetchLogs(employee.person_id, parseInt(e.target.value), 0);
                          }
                        }}
                        sx={{ minWidth: 90 }}
                      >
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={75}>75</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                      </Select>
                    </Stack>
                  </Stack>
                  <Pagination
                    count={Math.max(1, Math.ceil((logsPagination.total || 0) / logsPagination.limit))}
                    page={logsPagination.current_page}
                    onChange={handleLogsPageChange}
                    showFirstButton
                    showLastButton
                  />
                </Box>
              </Box>
            </Collapse>
          </Box>

          {/* Activity Logs Section */}
          <Box ref={activityLogsRef}>
            <Collapse in={expandedAccordion === 'logs-activity'} timeout={220} mountOnEnter unmountOnExit>
              <Box>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'medium', mb: 3 }}>
                  Activity Logs
                </Typography>

                {/* Activity Logs Filters */}
                <Box sx={pageFiltersToolbarSx}>
                  <Stack {...pageFiltersStackSx}>
                    <Box sx={pageFiltersControlsSx}>
                      <FormControl
                        size="small"
                        sx={pageFilterFormControlSx(220)}
                      >
                        <InputLabel id="activity-camera-label">Camera</InputLabel>
                        <Select
                          labelId="activity-camera-label"
                          label="Camera"
                          value={activityLogsCameraId}
                          onChange={(event) => {
                            setActivityLogsCameraId(event.target.value);
                            setActivityLogsPagination(prev => ({ ...prev, offset: 0, current_page: 1 }));
                          }}
                        >
                          <MenuItem value="">All Cameras</MenuItem>
                          {Array.isArray(activityLogsCameras) && activityLogsCameras.map((camera) => (
                            <MenuItem key={camera.camera_id || camera.label} value={camera.camera_id || ''}>
                              {camera.label || camera.camera_id || 'Activity Camera'}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Box
                        onClick={handleOpenActivityLogsDatePicker}
                        sx={pageFilterDatePickerSx(220)}
                      >
                        <CalendarMonth fontSize="small" color="action" />
                        <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                          {isSameDay(activityLogsDateRange[0].startDate, activityLogsDateRange[0].endDate)
                            ? `${format(activityLogsDateRange[0].startDate, 'MMM dd, yyyy')}${activityLogsStartTime !== '00:00' || activityLogsEndTime !== '23:59' ? ` • ${activityLogsStartTime} - ${activityLogsEndTime}` : ''}`
                            : `${format(activityLogsDateRange[0].startDate, 'MMM dd, yyyy')} - ${format(activityLogsDateRange[0].endDate, 'MMM dd, yyyy')}${activityLogsStartTime !== '00:00' || activityLogsEndTime !== '23:59' ? ` • ${activityLogsStartTime} - ${activityLogsEndTime}` : ''}`}
                        </Typography>
                      </Box>

                      <ClearAllFiltersButton
                        onClear={handleClearActivityLogsFilters}
                        disabled={!hasActiveActivityLogsFilters}
                      />
                    </Box>
                  </Stack>

                  <Popover
                    open={Boolean(activityLogsDatePickerAnchor)}
                    anchorEl={activityLogsDatePickerAnchor}
                    onClose={() => setActivityLogsDatePickerAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{
                      paper: {
                        sx: {
                          mt: 1,
                          p: 2,
                          borderRadius: 2,
                          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                          maxWidth: { xs: '95vw', sm: 'auto' }
                        }
                      }
                    }}
                  >
                    <Box>
                      <Box sx={{
                        '& .rdrCalendarWrapper': {
                          fontSize: { xs: '0.8rem', sm: '0.875rem' }
                        },
                        '& .rdrMonth': {
                          width: { xs: '280px', sm: '300px' },
                        },
                      }}>
                        <DateRangePicker
                          ranges={activityLogsDraftDateRange}
                          onChange={(item) => setActivityLogsDraftDateRange([item.selection])}
                          moveRangeOnFirstSelection={false}
                          months={1}
                          direction="horizontal"
                          rangeColors={['#40518A']}
                          showDateDisplay={false}
                          maxDate={new Date()}
                        />
                      </Box>

                      <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                        <TextField
                          label="Start Time"
                          type="time"
                          size="small"
                          value={activityLogsStartTimeDraft}
                          onChange={(e) => setActivityLogsStartTimeDraft(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 300 }}
                          sx={{ minWidth: { xs: '100%', sm: 120 } }}
                        />
                        <TextField
                          label="End Time"
                          type="time"
                          size="small"
                          value={activityLogsEndTimeDraft}
                          onChange={(e) => setActivityLogsEndTimeDraft(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 300 }}
                          sx={{ minWidth: { xs: '100%', sm: 120 } }}
                        />
                      </Box>

                      <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <Button size="small" variant="text" onClick={handleResetActivityLogsDateFilters}>
                          Reset
                        </Button>
                        <Button size="small" variant="outlined" onClick={() => setActivityLogsDatePickerAnchor(null)}>
                          Cancel
                        </Button>
                        <Button size="small" variant="contained" sx={{ ml: 1 }} onClick={handleApplyActivityLogsDateFilters}>
                          Apply
                        </Button>
                      </Box>
                    </Box>
                  </Popover>
                </Box>

                {/* Activity Logs Content */}
                {activityLogsLoading ? (
                  <Box>
                    {/* Date header skeleton */}
                    <Box sx={{ width: 180, height: 28, borderRadius: 1, mb: 2, ...shimmerStyles }} />
                    {/* Tiles skeleton - responsive grid */}
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'repeat(2, 1fr)',
                        sm: 'repeat(3, 1fr)',
                        md: 'repeat(4, 1fr)',
                        lg: 'repeat(5, 1fr)',
                      },
                      gap: 2,
                    }}>
                      {Array.from({ length: 12 }).map((_, idx) => (
                        <ActivityLogsTileSkeleton key={`activity-logs-skel-${idx}`} />
                      ))}
                    </Box>
                  </Box>
                ) : activityLogsError ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {activityLogsError}
                  </Alert>
                ) : activityLogsData.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No activity logs found for this employee
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    {Object.entries(groupActivityLogsByDate(activityLogsData)).map(([date, items]) => (
                      <Box key={date} sx={{ mb: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#333' }}>
                          {date}
                        </Typography>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                              xs: 'repeat(2, 1fr)',
                              sm: 'repeat(3, 1fr)',
                              md: 'repeat(4, 1fr)',
                              lg: 'repeat(5, 1fr)',
                            },
                            gap: 2,
                          }}
                        >
                          {items.map((log, index) => (
                            <AuditTile
                              key={`${log.audit_id || log.created_at}-${index}`}
                              log={log}
                              lazyLoad
                              disableEmployeeLink
                              capturedTs={{ iso: log.captured_at || log.created_at }}
                              timeOnly
                              disableRecordingPlayback
                              onClick={() => openTimingDialog({
                                log,
                                stack: [{ log, image: log.audit_id ? peekAuditImage(log.audit_id) : null }],
                              })}
                            />
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Activity Logs Pagination */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 2 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Showing {activityLogsPagination.total === 0 ? 0 : activityLogsPagination.offset + 1}-{Math.min(activityLogsPagination.offset + activityLogsPagination.limit, activityLogsPagination.total)} of {activityLogsPagination.total} results
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" color="text.secondary">Tiles per page:</Typography>
                      <Select
                        size="small"
                        value={activityLogsPagination.limit}
                        onChange={(e) => {
                          setActivityLogsPagination(prev => ({ ...prev, limit: parseInt(e.target.value), offset: 0, current_page: 1 }));
                          loadActivityLogs(parseInt(e.target.value), 0);
                        }}
                        sx={{ minWidth: 90 }}
                      >
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={75}>75</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                      </Select>
                    </Stack>
                  </Stack>
                  <Pagination
                    count={Math.max(1, Math.ceil((activityLogsPagination.total || 0) / activityLogsPagination.limit))}
                    page={activityLogsPagination.current_page}
                    onChange={handleActivityLogsPageChange}
                    showFirstButton
                    showLastButton
                  />
                </Box>
              </Box>
            </Collapse>
          </Box>
        </Box>
      </Box>

      {/* Logs Processing Timeline Modal */}
      <AuditProcessingTimelineModal
        timingDialog={timingDialog}
        closeTimingDialog={closeTimingDialog}
        isMobile={false}
        timingDialogResultMeta={timingDialog.log ? getResultMeta(timingDialog.log) : { label: 'Unknown', color: 'default' }}
        hasTimingDialogTrackAttendance={false}
        modalFrames={Array.isArray(timingDialog.stack) ? timingDialog.stack : []}
        imageCache={{}}
        ANONYMOUS_IMAGE="/anonymous.webp"
        showPossibleMatchActions={false}
        showProcessingTimeline={false}
        showTimingHeading={false}
        showTopMatches={false}
        title="Activity details"
      />

      {/* Attendance Export Dialog */}
      <UniversalModal
        open={attendanceExportDialogOpen}
        onClose={() => {
          setAttendanceExportDialogOpen(false);
          clearAttendanceExportMetaData();
        }}
        maxWidth="md"
        fullWidth
        title={`Export Attendance ${attendanceExportFormat.toUpperCase()}`}
        contentDividers
        footer={
          <>
            <Button onClick={() => {
              setAttendanceExportDialogOpen(false);
              clearAttendanceExportMetaData();
            }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleExportAttendance}
              disabled={exportLoading || attendanceExportSelectedFields.length === 0}
            >
              {exportLoading ? 'Exporting...' : `Export ${attendanceExportFormat.toUpperCase()}`}
            </Button>
          </>
        }
      >
        <Stack spacing={2}>
          <TextField
            label="File name"
            size="small"
            value={attendanceExportFileName}
            onChange={(e) => setAttendanceExportFileName(e.target.value)}
          />

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block' }}>
              Timestamp Date Filter
            </Typography>
            <Box
              onClick={(e) => setAttendanceExportDatePickerAnchor(e.currentTarget)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                border: '1px solid',
                borderColor: 'rgba(0, 0, 0, 0.23)',
                borderRadius: 1,
                px: 1.2,
                py: 0.95,
                cursor: 'pointer',
                '&:hover': { borderColor: 'rgba(0, 0, 0, 0.87)' },
              }}
            >
              <CalendarMonth fontSize="small" color="action" />
              <Typography variant="body2" sx={{ flex: 1, fontSize: '0.875rem' }}>
                {isSameDay(attendanceExportDateRange[0].startDate, attendanceExportDateRange[0].endDate)
                  ? format(attendanceExportDateRange[0].startDate, 'MMM dd, yyyy')
                  : `${format(attendanceExportDateRange[0].startDate, 'MMM dd')} - ${format(attendanceExportDateRange[0].endDate, 'MMM dd, yyyy')}`}
                {`  (${attendanceExportStartTime} - ${attendanceExportEndTime})`}
              </Typography>
            </Box>
            <Popover
              open={attendanceExportDatePickerOpen}
              anchorEl={attendanceExportDatePickerAnchor}
              onClose={() => setAttendanceExportDatePickerAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{
                paper: {
                  sx: { maxWidth: '100vw', overflow: 'auto' }
                }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                <Box sx={{
                  p: 2,
                  borderRight: { md: '1px solid' },
                  borderBottom: { xs: '1px solid', md: 'none' },
                  borderColor: 'divider',
                  minWidth: 140,
                }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Quick Select
                  </Typography>
                  <Stack spacing={0.5}>
                    {[
                      { label: 'Today', getValue: () => ({ startDate: new Date(), endDate: new Date() }) },
                      { label: 'Yesterday', getValue: () => ({ startDate: addDays(new Date(), -1), endDate: addDays(new Date(), -1) }) },
                      { label: 'Last 7 Days', getValue: () => ({ startDate: addDays(new Date(), -6), endDate: new Date() }) },
                      { label: 'Last 30 Days', getValue: () => ({ startDate: addDays(new Date(), -29), endDate: new Date() }) },
                      {
                        label: 'This Month', getValue: () => {
                          const now = new Date();
                          return { startDate: new Date(now.getFullYear(), now.getMonth(), 1), endDate: now };
                        }
                      },
                      {
                        label: 'Last Month', getValue: () => {
                          const now = new Date();
                          return { startDate: new Date(now.getFullYear(), now.getMonth() - 1, 1), endDate: new Date(now.getFullYear(), now.getMonth(), 0) };
                        }
                      },
                    ].map((preset) => (
                      <Button
                        key={preset.label}
                        size="small"
                        variant="text"
                        onClick={() => {
                          const { startDate, endDate } = preset.getValue();
                          setAttendanceExportDateRange([{ startDate, endDate, key: 'selection' }]);
                        }}
                        sx={{
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          px: 1.5,
                          py: 0.75,
                          color: 'text.primary',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        {preset.label}
                      </Button>
                    ))}
                  </Stack>
                </Box>

                <Box sx={{
                  '& .rdrCalendarWrapper': {
                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                  },
                  '& .rdrMonth': {
                    width: { xs: '280px', sm: '300px' },
                  },
                }}>
                  <DateRangePicker
                    ranges={attendanceExportDateRange}
                    onChange={(ranges) => setAttendanceExportDateRange([ranges.selection])}
                    moveRangeOnFirstSelection={false}
                    months={1}
                    direction="horizontal"
                    rangeColors={['#40518A']}
                    staticRanges={[]}
                    inputRanges={[]}
                    maxDate={new Date()}
                  />
                  <Box sx={{ px: 2, pb: 2, pt: 1, display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <TextField
                      label="Start Time"
                      type="time"
                      size="small"
                      value={attendanceExportStartTime}
                      onChange={(e) => setAttendanceExportStartTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 60 }}
                      fullWidth
                    />
                    <TextField
                      label="End Time"
                      type="time"
                      size="small"
                      value={attendanceExportEndTime}
                      onChange={(e) => setAttendanceExportEndTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 60 }}
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center', p: 2, pt: 0 }}>
                    <Button
                      onClick={() => {
                        const today = new Date();
                        setAttendanceExportDateRange([{ startDate: today, endDate: today, key: 'selection' }]);
                        setAttendanceExportStartTime('00:00');
                        setAttendanceExportEndTime('23:59');
                      }}
                      variant="outlined"
                      size="small"
                    >
                      Reset
                    </Button>
                    <Button onClick={() => setAttendanceExportDatePickerAnchor(null)} variant="outlined" size="small">
                      Cancel
                    </Button>
                    <Button
                      onClick={() => setAttendanceExportDatePickerAnchor(null)}
                      variant="contained"
                      size="small"
                    >
                      Apply
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Popover>
          </Box>

          <TextField
            label="Person Name"
            size="small"
            value={attendanceExportPersonName}
            InputProps={{
              readOnly: true,
            }}
            disabled={attendanceExportAllEmployeesPresence}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={attendanceExportAllEmployeesPresence}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setAttendanceExportAllEmployeesPresence(checked);
                  setAttendanceExportSelectedFields((prev) => {
                    if (checked) {
                      return prev.includes('presence_time') ? prev : [...prev, 'presence_time'];
                    }
                    return prev.filter((field) => field !== 'presence_time');
                  });
                }}
              />
            }
            label="Presence Time (Duration)"
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <FormControl size="small" fullWidth>
              <InputLabel id="export-action-label" shrink>
                Select Action
              </InputLabel>
              <Select
                labelId="export-action-label"
                value={attendanceExportAction}
                label="Select Action"
                onChange={(e) => setAttendanceExportAction(e.target.value)}
                displayEmpty
                renderValue={(v) => v ? (v === 'check_in' ? 'Check-In' : v === 'check_out' ? 'Check-Out' : 'All') : 'All'}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="check_in">Check-In</MenuItem>
                <MenuItem value="check_out">Check-Out</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel id="export-premises-label" shrink>
                Premises Status
              </InputLabel>
              <Select
                labelId="export-premises-label"
                value={attendanceExportPremisesFilter}
                label="Premises Status"
                onChange={(e) => setAttendanceExportPremisesFilter(e.target.value)}
                displayEmpty
                renderValue={(v) => v ? (v === 'in' ? 'In Premises' : 'Out of Premises') : 'All Status'}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="in">In Premises</MenuItem>
                <MenuItem value="out">Out of Premises</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel id="export-late-filter-label" shrink>
                Late Filter
              </InputLabel>
              <Select
                labelId="export-late-filter-label"
                value={attendanceExportLateFilter ? 'late' : ''}
                label="Late Filter"
                onChange={(e) => setAttendanceExportLateFilter(e.target.value === 'late')}
                displayEmpty
                renderValue={(v) => v === 'late' ? 'Late Entries' : 'All Entries'}
              >
                <MenuItem value="">All Entries</MenuItem>
                <MenuItem value="late">Late Entries</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Divider />

          <Typography variant="subtitle2">Columns (select + order)</Typography>
          <List dense sx={{ maxHeight: 220, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {(() => {
              const fieldByName = new Map(attendanceExportFields.map((field) => [field.field, field]));
              const selectedFieldsInOrder = attendanceExportSelectedFields
                .map((fieldName) => fieldByName.get(fieldName))
                .filter(Boolean);
              const unselectedFields = attendanceExportFields.filter(
                (field) => !attendanceExportSelectedFields.includes(field.field)
              );
              const orderedFields = [...selectedFieldsInOrder, ...unselectedFields];

              return orderedFields.map((field) => {
                const selected = attendanceExportSelectedFields.includes(field.field);
                const selectedIndex = attendanceExportSelectedFields.indexOf(field.field);
                return (
                  <ListItem
                    key={field.field}
                    secondaryAction={selected ? (
                      <Stack direction="row" spacing={0.5}>
                        <IconButton size="small" onClick={() => moveSelectedField(selectedIndex, 'up')} disabled={selectedIndex <= 0}>
                          <ArrowUpward fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => moveSelectedField(selectedIndex, 'down')}
                          disabled={selectedIndex < 0 || selectedIndex >= attendanceExportSelectedFields.length - 1}
                        >
                          <ArrowDownward fontSize="small" />
                        </IconButton>
                      </Stack>
                    ) : null}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox
                        edge="start"
                        checked={selected}
                        onChange={(e) => toggleAttendanceExportField(field.field, e.target.checked)}
                      />
                    </ListItemIcon>
                    <ListItemText primary={field.label || field.field} />
                  </ListItem>
                );
              });
            })()}
          </List>
        </Stack>
      </UniversalModal>
    </>
  );
};

export default EmployeeDetails;
